import type { ComponentChildren } from 'preact';
import { nameFromEmail, type DeliverySpec } from '@kff/domain';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { ApiError } from '../api.js';
import { ddm, ddmSv, h } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { activeCount, advancedCount, filterLabels, filterOptions, hasMyRole, isDone, matches, NO_FILTERS, readFilters, toggleView, VIEWS, viewOn, writeFilters, type DueFilter, type ProjectFilters, type SourceFilter, type WaitFilter } from '../projectFilters.js';
import { Icon, PageTabs } from '../shell.js';
import { ProjectNotes } from './Notes.js';
import { ProjectBriefTab } from './ProjectBrief.js';
import { BookShoot, ProjectShoots } from './ProjectShoots.js';
import { ProjectActors } from './Actors.js';
import { ProjectFiles } from './ProjectFiles.js';
import { stageCount, StageTaskPanel, useStageTasks, type TaskStage } from './stageTasks.js';
import { ProjectActivity, useHashFocus } from './projectActivity.js';
import type { AgreementRow } from './Agreements.js';
import { agreementLine } from './Clients.js';
import { contractFact, contractsOf, contractWhat } from './Contracts.js';
import { startViewAs, viewAsAllowed } from '../viewAs.js';
import { AddBall, PersonPicker } from '../personPicker.js';
import { SendForReview } from './sendForReview.js';
import { DeliveryBox, type BillTo, type DeliveryInfo } from './delivery.js';
import { ProjectArchive, type ArchiveRow } from './projectArchive.js';
import { OpenBeside, openBesideLabel } from './sideBySide.js';
import { setProjectHidden } from './hideProject.js';
import { timerChanged } from '../timer.js';
import { daysShort, openPlan, PT, ProjectPlan } from '../planTime.js';
import { CancelledBanner, CancelProjectDialog, RemoveProjectDialog, RemoveVideoDialog, removeTexts } from './removeProject.js';
import { Avatar, AvatarStack, Callout, Dialog, ErrorBox, LoadError, Loading, MoreOptions, PageHead, PersonChip, RolePeople, RowMenu, toast } from '../ui.js';

/**
 * Projects (Del 1 §1.5.3, UX-114, UX-120–122): the list grouped by delivery month, New project, and the project page with
 * Videos · Files · Activity · Details. Step changes are the manual-mode buttons (Del 2 §2.6.1); every change has Undo.
 * Monday stays the source for real projects while this module is off in the Fas 0 preset.
 */

export interface StageView {
  stage: string;
  label: string;
  tag: string | null;
  detail: string;
  position: number;
}
export interface NextStep {
  text: string;
  phrase: string;
  mine: boolean;
  waitingOn?: string;
  due: string | null;
  /** Who the step waits on (older API versions leave it out). */
  who?: { id: string; name: string }[];
}
export interface ProjectSummary {
  id: string;
  code: string;
  name: string;
  client: { id: string; name: string; code: string };
  deliveryMonth: string;
  status: string;
  stage: StageView;
  next: NextStep;
  nextDeadline: string | null;
  producer: string | null;
  /** Everyone on the project; older API versions leave it out, and the Person filter then hides. */
  people?: { id: string; name: string; role: string; roles?: string[] }[];
  mine: boolean;
  videos: { total: number; delivered: number };
  /** Imported from Monday (Del 6 §6.3.1). */
  fromMonday?: boolean;
  isTest?: boolean;
  /** List only (older API versions leave these out): the type, the shoot column, and what the viewer may do from the row. */
  productionType?: string;
  shoot?: { date: string; monday: boolean; past: boolean; mondaySays?: string | null } | null;
  allow?: { team: boolean; book: boolean };
  /** List only: the videos under the row's arrow, "Din tur", hidden from My work, and my roles (older API versions leave them out). */
  videoList?: ListVideo[];
  myTurn?: boolean;
  hidden?: boolean;
  myRoleKeys?: string[];
  /** your own planned time the coming two weeks (owner 2026-11-03) */
  myPlan?: { hours: number; days: string[] } | null;
}
/** A video under a row of the projects list (owner 2026-11-03: one list for every role). */
export interface ListVideo {
  id: string;
  name: string;
  editor: string | null;
  stage: StageView;
  next: NextStep;
  due: string | null;
  mine: boolean;
  /** my own next step on the video, first in its ⋯ */
  forward: { key: string; label: string } | null;
}
interface Command {
  key: string;
  label: string;
  needsReason: boolean;
}
export interface VideoView {
  id: string;
  code: string;
  seq: number;
  name: string;
  phase: string;
  step: string;
  stage: StageView;
  next: NextStep;
  customerRound: number;
  editor: { id: string; name: string } | null;
  needsFinalFormats: boolean;
  firstDraftDue: string | null;
  nextDraftDue: string | null;
  internalDeadline: string | null;
  overrideReason: string | null;
  deliveredAt: string | null;
  cancelReason: string | null;
  commands: Command[];
  /** The command that moves the video on (older API versions leave it out). */
  forward?: string | null;
  /** The Monday label the step was imported from. */
  mondayLabel?: string | null;
}
interface Contact {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  canApprove: boolean;
  canReview: boolean;
  active: boolean;
}
export interface ProjectDetail extends Omit<ProjectSummary, 'videos'> {
  kind: string;
  productionType: string;
  creativePackage: string | null;
  bookingStatus: string;
  decider: Contact | null;
  contacts: Contact[];
  team: { role: string; label: string; people: { id: string; name: string; isPrimary: boolean }[] }[];
  videos: VideoView[];
  can: { edit: boolean; editProducer: boolean; editContacts: boolean; markShootDone: boolean; cancel?: boolean; removeVideo?: boolean };
  /** cancelled (in the Hub, or in Monday): when and why */
  cancelled?: { at: string; reason: string | null } | null;
  /** Monday owns the project while it is the source: the Hub only shows it. */
  mondayOwned?: boolean;
  /** a shoot is booked (Hub or Monday's date): "Boka inspelning" moves into ⋯ */
  shootBooked?: boolean;
  /** a Hub project mirrored to Monday (one way) */
  mondayMirror?: { itemUrl: string | null; pushedAt: string | null; error: string | null; warnings: string[] } | null;
  /** how much the Hub emails the client about review and delivery (owner 2026-10-24); staff employees only */
  clientMail?: { level: ClientMail; by: 'company' | 'client' | 'project' | null; project: ClientMail | null; client: ClientMail; company: boolean; ziflow: boolean } | null;
  /** which of the client's companies in Bokio the project is invoiced to; for a client in Bokio (staff employees) */
  billTo?: BillTo | null;
  /** the delivery spec (owner 2026-11-01) and the delivery time that applies */
  spec?: DeliverySpec;
  delivery?: DeliveryInfo | null;
}
interface Options {
  people: Record<'producer' | 'cd' | 'editor' | 'camera', { id: string; name: string; freelancer: boolean }[]>;
  clients: { id: string; name: string; code: string }[];
  productionTypes: string[];
  packages: string[];
  bookingStatuses: string[];
  phases: { phase: string; steps: string[] }[];
  me: { id: string };
}
type ClientMail = 'on' | 'no_reminders' | 'off';
export type Change = { project: ProjectDetail; undoId?: string | null; text: string };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** This page's texts; Swedish is typed against English (see i18n.ts). API texts (stages, next steps, commands) stay as sent. */
const TX = texts(
  {
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    type: { shoot: 'Shoot', animation: 'Animation', motion: 'Motion graphics', screen: 'Screen recording', stock: 'Stock', ai: 'AI', ads: 'Ads', live: 'Live', photo: 'Photo' } as Record<string, string>,
    bookings: { not_started: 'Not started', preliminary: 'Preliminary', booked: 'Booked', reschedule: 'Reschedule', paused: 'Paused', cancelled: 'Cancelled' } as Record<string, string>,
    step: {
      planned: 'Planned',
      ready_to_edit: 'Ready to edit',
      editing: 'Editing',
      internal_review: 'Internal review',
      internal_changes: 'Internal changes',
      producer_review: 'Producer review',
      with_customer: 'With client',
      customer_changes: 'Client changes',
      approved: 'Approved',
      delivered: 'Delivered',
      cancelled: 'Cancelled'
    } as Record<string, string>,
    phases: { storyboard: 'Storyboard', draft: 'Draft', final: 'Final formats' } as Record<string, string>,
    due: (d: string) => `Due ${d}`,
    journey: [
      { title: 'Planning', text: 'Brief, script and shoot are planned and done.' },
      { title: 'Editing', text: 'The editor cuts the video from the material.' },
      { title: 'Internal review', text: 'CD and producer check quality before the client sees it.' },
      { title: 'With client', text: 'The client reviews and gives feedback.' },
      { title: 'Delivered', text: 'Final files and formats go to the client.' }
    ],
    stages: 'Stages',
    stagesColon: 'Stages:',
    whereProject: 'Where the project is',
    stageOf: (n: number, of: number) => `Stage ${n} of ${of}`,
    readyToDeliver: 'Ready to deliver',
    weAreHere: 'We are here',
    undone: 'Undone',
    // list
    projects: 'Projects',
    crewSub: (n: number, mine: boolean) => `${n} running project${n === 1 ? '' : 's'}${mine ? ' you are on' : ' at KFF'}`,
    myRole: 'My role',
    roleName: { producer: 'Producer', editor: 'Editing', camera: 'Camera' } as Record<string, string>,
    hiddenView: (n: number) => `Hidden (${n})`,
    openVideos: (name: string) => `Show the videos in ${name}`,
    videosN: (n: number) => `${n} video${n === 1 ? '' : 's'}`,
    notOn: 'You are not on this project',
    uploadDraft: 'Upload new draft…',
    openReviewOf: 'Open the review',
    startTimer: 'Start timer on the video',
    timerOn: (video: string) => `Timer started on ${video}`,
    readBrief: 'Read the brief',
    hideInMyWork: 'Hide from My work',
    showInMyWork: 'Show in My work again',
    hiddenNow: (name: string) => `${name} is hidden from My work`,
    shownNow: (name: string) => `${name} shows in My work again`,
    listSub: (open: number, from: string, to: string) => `${open} open projects · ${from} – ${to}`,
    newProject: '+ New project',
    filterProjects: 'Filter projects',
    show: 'Show',
    mine: 'Mine',
    all: 'All',
    archive: 'Archive',
    archiveSub: 'Every project, the Hub’s and Monday’s since 2021',
    olderInArchive: 'Older and delivered projects, and Monday’s history since 2021, are in the Archive.',
    openArchive: 'Open the Archive',
    searchPlaceholder: 'Search project, client, code or person',
    searchLabel: 'Search projects',
    advanced: 'Advanced filters',
    quickViews: 'Quick filters',
    stage: 'Stage',
    client: 'Client',
    person: 'Person',
    deadline: 'Deadline',
    waitingOn: 'Waiting on',
    source: 'Source',
    deliveryFrom: 'Delivery from',
    deliveryTo: 'Delivery to',
    showDone: (n: number) => `Show delivered and cancelled (${n})`,
    showing: (n: number, of: number) => `Showing ${n} of ${of}`,
    clearFilters: 'Clear filters',
    noMatch: 'No projects match these filters',
    noMatchMine: 'Try All, or clear a filter.',
    noMatchAll: 'Clear a filter to see more.',
    nothingOpen: 'Nothing open here',
    noneOfYours: 'No projects of yours here',
    noneYet: 'No projects yet',
    allDone: 'Every project here is delivered or cancelled.',
    switchAll: 'Switch to All to see every project.',
    autoAll: 'You are not on any project right now, so all projects are shown.',
    mondayStill: 'New projects show up here.',
    thisMonth: 'This month',
    past: 'Past',
    projectsIn: (m: string) => `Projects in ${m}`,
    project: 'Project',
    nextStep: 'Next step',
    team: 'Team',
    colProducer: 'Producer',
    colEditing: 'Editing',
    colCamera: 'Camera',
    colShoot: 'Shoot',
    bookShootShort: 'Book',
    bookShootOn: (project: string) => `Book a shoot on ${project}`,
    setRoleOn: (role: string, project: string) => `${role} on ${project}: change`,
    shootFromMonday: 'Shoot day from Monday',
    mondaySays: (d: string) => `Monday says ${d} – change the shoot day in Monday`,
    shootDone: 'Last shoot',
    searchPerson: 'Search name…',
    noOneMatches: 'No one matches',
    pickHint: 'Click to add, click again to remove',
    fromMonday: (code: string) => `${code} · from Monday`,
    any: 'Any',
    notInList: '(not in this list)',
    // new project
    newProjectTitle: 'New project',
    reopenTitle: 'Open in the Hub again',
    reopenNote: (month: string) => `From Monday’s project (${month}): the client, the name and the Drive and Ziflow links come along. Monday’s history stays as it is.`,
    cancel: 'Cancel',
    createProject: 'Create project',
    created: (name: string) => `${name} created`,
    choose: 'Choose…',
    newClient: '+ New client…',
    clientName: 'Client name',
    projectName: 'Project name',
    deliveryMonth: 'Delivery month',
    agreementLbl: 'Agreement',
    noAgreement: 'No signed agreement with the client',
    createQuote: 'Create a quote',
    videos: 'Videos',
    productionType: 'Production type',
    package: 'Package',
    notSet: 'Not set',
    producer: 'Producer',
    nobodyYet: 'Nobody yet',
    teamInMonday: 'The team is set in Monday for this project.',
    finalsAfter: 'Final formats after approval',
    testProject: 'Test project',
    clientEnglish: 'The new client gets emails in English',
    testHint: 'Its Drive folders go to the folder zz_Hubben test, and it never goes to Monday.',
    testTag: 'Test',
    // project page
    backToProjects: '← Projects',
    headSub: (month: string, n: number, delivered: number) => `Delivery ${month} · ${plural(n, 'video', 'videos')}${delivered ? ` · ${delivered} delivered` : ''}`,
    mondayTitle: 'From Monday – read-only here',
    mondayBody: 'Change this project in Monday. The Hub shows the change within 15 minutes.',
    whatNow: 'What happens now',
    markShootDone: 'Mark shoot done',
    bookShoot: 'Book shoot',
    bookAnotherShoot: 'Book another shoot',
    moreOptions: 'More options',
    addVideoMenu: 'Add video…',
    addVideo: 'Add video',
    name: 'Name',
    namePlaceholder: 'e.g. Teaser 9:16',
    moreFor: (name: string) => `More for ${name}`,
    noVideos: 'No videos yet',
    addVideosHint: 'Add them with Add video… when you know how many.',
    videosUnknown: 'Not known yet',
    videosHint: 'Leave empty if you don’t know yet – add videos on the project later.',
    editVideoMenu: 'Edit video…',
    openReview: 'Review',
    droppedVideos: 'Dropped videos',
    inMonday: (label: string) => `In Monday: ${label}`,
    forced: (reason: string) => `Moved by hand: ${reason}`,
    editor: 'Editor',
    noEditor: 'No editor yet',
    phase: 'Phase',
    stepLabel: 'Step',
    reason: 'Reason',
    reasonHint: "Kept in the project's history.",
    editTitle: (name: string) => `Edit ${name}`,
    save: 'Save',
    freelanceParen: ' (freelance)',
    internalDeadline: 'Internal deadline',
    firstDraftDue: 'First draft due',
    activity: 'Activity',
    nothingYet: 'Nothing yet.',
    // details
    about: 'About the project',
    projectCode: 'Project code',
    booking: 'Booking',
    videosOf: (d: number, n: number) => `${d} of ${n} delivered`,
    noneYetShort: 'None yet',
    importedMonday: 'Imported from Monday',
    mirror: 'In Monday',
    mirrorOpen: 'Open the item in Monday',
    clientMail: 'Emails to the client',
    clientMailOpt: { inherit: (c: string) => `As the client (${c})`, on: 'On', no_reminders: 'No reminders', off: 'Off – I send them myself' } as Record<string, string | ((c: string) => string)>,
    clientMailLevel: { on: 'on', no_reminders: 'no reminders', off: 'off' } as Record<ClientMail, string>,
    clientMailBy: (by: 'company' | 'client'): string => (by === 'company' ? 'Off for the whole company (Settings › Sign-in and email).' : 'The client card is stricter, so that applies.'),
    clientMailHint: 'Review emails, reminders and delivery. The brief and shoot invites are always sent by you.',
    clientMailZiflow: 'Reviewed in Ziflow: Ziflow and Make send the emails until the next version is sent from the Hub.',
    clientMailSaved: 'Saved',
    billTo: 'Invoiced to',
    billToMain: (n: string) => `${n} (main company)`,
    mirrorPending: 'Not in Monday yet – the next sync creates the item',
    mirrorSynced: (at: string) => `Updated from the Hub ${at}. Change the project here, not in Monday.`,
    mirrorFailed: (e: string) => `The last update to Monday failed: ${e}`,
    change: 'Change',
    add: 'Add',
    changeRole: (label: string) => `Change ${label}`,
    primary: 'Primary',
    clientContacts: 'Client contacts',
    addContactPlus: '+ Add contact',
    missingDecider: 'No approver yet',
    pickDecider: 'Pick who approves the videos for the client.',
    makeDecider: 'Make approver',
    portalLinkMenu: 'Portal link…',
    viewAsClient: 'View as client',
    decider: 'Approver',
    noContacts: 'No contacts yet.',
    contactAdded: 'Contact added',
    rename: 'Rename',
    teamOn: (label: string, project: string) => `${label} on ${project}`,
    freelance: 'freelance',
    primaryLower: 'primary',
    nobodyHasRole: (label: string) => `Nobody has the ${label} role yet – add it in People & access.`,
    // portal link
    portalCopied: 'Portal link copied – nothing was emailed',
    portalCreated: 'New portal link created – copy it from the field',
    portalRevoked: (who: string) => `Revoked – ${who}'s portal link no longer works`,
    portalTitle: (who: string) => `Portal link for ${who}`,
    revokeLink: 'Revoke link',
    revokeSure: (who: string) => `Revoke ${who}'s portal link? It stops working at once.`,
    copyNewLink: 'Copy a new link',
    copyLink: 'Copy link',
    portalBody: (who: string) =>
      `${who} sees the client's projects and videos in plain language – no internal steps, names or prices. Send the link yourself; the Hub emails nothing yet. A new link stops the previous one.`,
    loading: 'Loading…',
    newLinkFor: (who: string) => `New link – send it to ${who}`,
    // contact
    addContact: 'Add contact',
    pickCompany: 'Choose a company',
    company: 'Company',
    jobTitle: 'Title',
    email: 'Email',
    phone: 'Phone',
    contactNameHint: 'Taken from the address. Change it if it is wrong.',
    canApprove: 'Approves films',
    editContact: 'Edit contact',
    edit: 'Edit',
    billingContact: 'Receives invoices',
    formerContact: 'Former contact (no longer at the company)',
    contactSaved: 'Contact saved'
  },
  {
    months: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'],
    type: { shoot: 'Inspelning', animation: 'Animation', motion: 'Motion graphics', screen: 'Skärminspelning', stock: 'Arkivmaterial', ai: 'AI', ads: 'Annonser', live: 'Live', photo: 'Foto' },
    bookings: { not_started: 'Inte påbörjad', preliminary: 'Preliminär', booked: 'Bokad', reschedule: 'Bokas om', paused: 'Pausad', cancelled: 'Inställd' },
    step: {
      planned: 'Planerad',
      ready_to_edit: 'Redo för redigering',
      editing: 'Redigering',
      internal_review: 'Intern granskning',
      internal_changes: 'Interna ändringar',
      producer_review: 'Producentgranskning',
      with_customer: 'Hos kund',
      customer_changes: 'Kundens ändringar',
      approved: 'Godkänd',
      delivered: 'Levererad',
      cancelled: 'Avbruten'
    },
    phases: { storyboard: 'Storyboard', draft: 'Utkast', final: 'Slutformat' },
    due: (d: string) => `Deadline ${d}`,
    journey: [
      { title: 'Planering', text: 'Brief, manus och inspelning planeras och genomförs.' },
      { title: 'Redigering', text: 'Redigeraren sätter ihop videon av materialet.' },
      { title: 'Intern granskning', text: 'CD och producent kollar kvaliteten innan kunden ser den.' },
      { title: 'Hos kund', text: 'Kunden granskar och ger feedback.' },
      { title: 'Levererad', text: 'Slutfiler och format går till kunden.' }
    ],
    stages: 'Steg',
    stagesColon: 'Steg:',
    whereProject: 'Var projektet är',
    stageOf: (n: number, of: number) => `Steg ${n} av ${of}`,
    readyToDeliver: 'Redo att leverera',
    weAreHere: 'Här är vi',
    undone: 'Ångrat',
    projects: 'Projekt',
    crewSub: (n: number, mine: boolean) => `${n} ${n === 1 ? 'aktivt projekt' : 'aktiva projekt'}${mine ? ' där du är med' : ' på KFF'}`,
    myRole: 'Min roll',
    roleName: { producer: 'Producent', editor: 'Redigering', camera: 'Kamera' } as Record<string, string>,
    hiddenView: (n: number) => `Dolda (${n})`,
    openVideos: (name: string) => `Visa videorna i ${name}`,
    videosN: (n: number) => `${n} ${n === 1 ? 'video' : 'videor'}`,
    notOn: 'Du är inte med i det här projektet',
    uploadDraft: 'Ladda upp nytt utkast…',
    openReviewOf: 'Öppna granskningen',
    startTimer: 'Starta timer på videon',
    timerOn: (video: string) => `Timer startad på ${video}`,
    readBrief: 'Läs briefen',
    hideInMyWork: 'Dölj i Mitt arbete',
    showInMyWork: 'Visa i Mitt arbete igen',
    hiddenNow: (name: string) => `${name} är dolt från Mitt arbete`,
    shownNow: (name: string) => `${name} syns i Mitt arbete igen`,
    listSub: (open: number, from: string, to: string) => `${open} öppna projekt · ${from} – ${to}`,
    newProject: '+ Nytt projekt',
    filterProjects: 'Filtrera projekt',
    show: 'Visa',
    mine: 'Mina',
    all: 'Alla',
    archive: 'Arkiv',
    archiveSub: 'Alla projekt, Hubbens och Mondays sedan 2021',
    olderInArchive: 'Äldre och levererade projekt, och Mondays historik sedan 2021, finns i Arkiv.',
    openArchive: 'Öppna Arkiv',
    searchPlaceholder: 'Sök projekt, kund, kod eller person',
    searchLabel: 'Sök projekt',
    advanced: 'Avancerade filter',
    quickViews: 'Snabbfilter',
    stage: 'Steg',
    client: 'Kund',
    person: 'Person',
    deadline: 'Deadline',
    waitingOn: 'Väntar på',
    source: 'Källa',
    deliveryFrom: 'Leverans från',
    deliveryTo: 'Leverans till',
    showDone: (n: number) => `Visa levererade och avbrutna (${n})`,
    showing: (n: number, of: number) => `Visar ${n} av ${of}`,
    clearFilters: 'Rensa filter',
    noMatch: 'Inga projekt matchar filtren',
    noMatchMine: 'Testa Alla, eller ta bort ett filter.',
    noMatchAll: 'Ta bort ett filter för att se fler.',
    nothingOpen: 'Inget öppet här',
    noneOfYours: 'Inga av dina projekt här',
    noneYet: 'Inga projekt än',
    allDone: 'Alla projekt här är levererade eller avbrutna.',
    switchAll: 'Byt till Alla för att se alla projekt.',
    autoAll: 'Du är inte med i något projekt just nu, så alla projekt visas.',
    mondayStill: 'Nya projekt syns här.',
    thisMonth: 'Den här månaden',
    past: 'Passerad',
    projectsIn: (m: string) => `Projekt i ${m}`,
    project: 'Projekt',
    nextStep: 'Nästa steg',
    team: 'Team',
    colProducer: 'Producent',
    colEditing: 'Redigering',
    colCamera: 'Kamera',
    colShoot: 'Inspelning',
    bookShootShort: 'Boka',
    bookShootOn: (project: string) => `Boka inspelning på ${project}`,
    setRoleOn: (role: string, project: string) => `${role} på ${project}: ändra`,
    shootFromMonday: 'Inspelningsdag från Monday',
    mondaySays: (d: string) => `Monday säger ${d} – ändra inspelningsdagen i Monday`,
    shootDone: 'Senaste inspelningen',
    searchPerson: 'Sök namn…',
    noOneMatches: 'Ingen matchar',
    pickHint: 'Klicka för att lägga till, igen för att ta bort',
    fromMonday: (code: string) => `${code} · från Monday`,
    any: 'Alla',
    notInList: '(finns inte i listan)',
    newProjectTitle: 'Nytt projekt',
    reopenTitle: 'Öppna i Hubben igen',
    reopenNote: (month: string) => `Från Monday-projektet (${month}): kunden, namnet och länkarna till Drive och Ziflow följer med. Mondays historik ligger kvar som den är.`,
    cancel: 'Avbryt',
    createProject: 'Skapa projekt',
    created: (name: string) => `${name} skapat`,
    choose: 'Välj…',
    newClient: '+ Ny kund…',
    clientName: 'Kundnamn',
    projectName: 'Projektnamn',
    deliveryMonth: 'Leveransmånad',
    agreementLbl: 'Avtal',
    noAgreement: 'Inget signerat avtal med kunden',
    createQuote: 'Skapa offert',
    videos: 'Videor',
    productionType: 'Produktionstyp',
    package: 'Paket',
    notSet: 'Inte valt',
    producer: 'Producent',
    nobodyYet: 'Ingen än',
    teamInMonday: 'Teamet styrs i Monday för det här projektet.',
    finalsAfter: 'Slutformat efter godkännande',
    testProject: 'Testprojekt',
    clientEnglish: 'Den nya kunden får utskick på engelska',
    testHint: 'Drive-mapparna hamnar i mappen zz_Hubben test, och projektet skickas aldrig till Monday.',
    testTag: 'Test',
    backToProjects: '← Projekt',
    headSub: (month: string, n: number, delivered: number) => `Leverans ${month} · ${plural(n, 'video', 'videor')}${delivered ? ` · ${delivered} levererade` : ''}`,
    mondayTitle: 'Från Monday – bara läsläge här',
    mondayBody: 'Ändra projektet i Monday. Hubben visar ändringen inom 15 minuter.',
    whatNow: 'Vad händer nu',
    markShootDone: 'Markera inspelning klar',
    bookShoot: 'Boka inspelning',
    bookAnotherShoot: 'Boka en inspelning till',
    moreOptions: 'Fler val',
    addVideoMenu: 'Lägg till video…',
    addVideo: 'Lägg till video',
    name: 'Namn',
    namePlaceholder: 't.ex. Teaser 9:16',
    moreFor: (name: string) => `Mer för ${name}`,
    noVideos: 'Inga videor än',
    addVideosHint: 'Lägg till dem med Lägg till video… när ni vet hur många.',
    videosUnknown: 'Vet inte än',
    videosHint: 'Lämna tomt om ni inte vet än – lägg till videor på projektet senare.',
    editVideoMenu: 'Ändra video…',
    openReview: 'Granskning',
    droppedVideos: 'Strukna videor',
    inMonday: (label: string) => `I Monday: ${label}`,
    forced: (reason: string) => `Flyttad för hand: ${reason}`,
    editor: 'Redigerare',
    noEditor: 'Ingen redigerare än',
    phase: 'Fas',
    stepLabel: 'Steg',
    reason: 'Anledning',
    reasonHint: 'Sparas i projektets historik.',
    editTitle: (name: string) => `Ändra ${name}`,
    save: 'Spara',
    freelanceParen: ' (frilans)',
    internalDeadline: 'Intern deadline',
    firstDraftDue: 'Deadline första utkast',
    activity: 'Aktivitet',
    nothingYet: 'Inget än.',
    about: 'Om projektet',
    projectCode: 'Projektkod',
    booking: 'Bokning',
    videosOf: (d: number, n: number) => `${d} av ${n} levererade`,
    noneYetShort: 'Inga än',
    importedMonday: 'Importerat från Monday',
    mirror: 'I Monday',
    mirrorOpen: 'Öppna i Monday',
    clientMail: 'Utskick till kunden',
    clientMailOpt: { inherit: (c: string) => `Som kunden (${c})`, on: 'På', no_reminders: 'Inga påminnelser', off: 'Av – jag skickar själv' } as Record<string, string | ((c: string) => string)>,
    clientMailLevel: { on: 'på', no_reminders: 'inga påminnelser', off: 'av' } as Record<ClientMail, string>,
    clientMailBy: (by: 'company' | 'client') => (by === 'company' ? 'Avstängt för hela bolaget (Inställningar › Inloggning och e-post).' : 'Kundkortet är strängare, så det gäller.'),
    clientMailHint: 'Granskningsmejl, påminnelser och leverans. Brief och inspelningsinbjudan skickar du alltid själv.',
    clientMailZiflow: 'Granskas i Ziflow: Ziflow och Make sköter utskicken tills nästa version skickas från Hubben.',
    clientMailSaved: 'Sparat',
    billTo: 'Faktureras till',
    billToMain: (n: string) => `${n} (huvudbolag)`,
    mirrorPending: 'Inte i Monday än – nästa synk skapar raden',
    mirrorSynced: (at: string) => `Uppdaterad från Hubben ${at}. Ändra projektet här, inte i Monday.`,
    mirrorFailed: (e: string) => `Senaste uppdateringen till Monday misslyckades: ${e}`,
    change: 'Ändra',
    add: 'Lägg till',
    changeRole: (label: string) => `Ändra ${label}`,
    primary: 'Huvudansvarig',
    clientContacts: 'Kundkontakter',
    addContactPlus: '+ Lägg till kontakt',
    missingDecider: 'Ingen godkännare än',
    pickDecider: 'Välj vem som godkänner videorna åt kunden.',
    makeDecider: 'Gör till godkännare',
    portalLinkMenu: 'Portallänk…',
    viewAsClient: 'Visa som kund',
    decider: 'Godkännare',
    noContacts: 'Inga kontakter än.',
    contactAdded: 'Kontakten är tillagd',
    rename: 'Byt namn',
    teamOn: (label: string, project: string) => `${label} på ${project}`,
    freelance: 'frilans',
    primaryLower: 'huvudansvarig',
    nobodyHasRole: (label: string) => `Ingen har rollen ${label} än – lägg till den i Personer och behörighet.`,
    portalCopied: 'Portallänken är kopierad – inget mejl skickades',
    portalCreated: 'Ny portallänk skapad – kopiera den från fältet',
    portalRevoked: (who: string) => `Återkallad – ${who}s portallänk fungerar inte längre`,
    portalTitle: (who: string) => `Portallänk för ${who}`,
    revokeLink: 'Återkalla länk',
    revokeSure: (who: string) => `Återkalla portallänken för ${who}? Den slutar fungera direkt.`,
    copyNewLink: 'Kopiera en ny länk',
    copyLink: 'Kopiera länk',
    portalBody: (who: string) =>
      `${who} ser kundens projekt och videor i klartext – inga interna steg, namn eller priser. Skicka länken själv; Hubben mejlar inget än. En ny länk stänger av den förra.`,
    loading: 'Laddar…',
    newLinkFor: (who: string) => `Ny länk – skicka den till ${who}`,
    addContact: 'Lägg till kontakt',
    pickCompany: 'Välj företag',
    company: 'Företag',
    jobTitle: 'Titel',
    email: 'E-post',
    phone: 'Telefon',
    contactNameHint: 'Hämtat från adressen. Ändra om det blir fel.',
    canApprove: 'Godkänner filmer',
    editContact: 'Ändra kontakt',
    edit: 'Ändra',
    billingContact: 'Får fakturor',
    formerContact: 'Tidigare kontakt (har slutat)',
    contactSaved: 'Kontakten är sparad'
  }
);

/** "October 2026" (Swedish "oktober 2026"; capitalise it where it starts a heading). */
export const monthLabel = (m: string, lang: Lang = 'en') => `${TX[lang].months[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}`;
const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
/** Change requests stay in ⋯ so the row's one button always moves the video forward. */
const BACKWARD = ['internal_changes', 'client_changes'];
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));
const val = (e: Event) => (e.target as HTMLInputElement).value;

/** UX-018: five segments with text and aria-label – never colour alone. Ready to deliver shows the last segment outlined. */
export function StageBar(p: { stage: StageView; compact?: boolean }) {
  const s = p.stage;
  return (
    <span class={`stagebar st-${s.stage}${s.stage === 'cancelled' ? ' is-cancelled' : ''}`} role="img" aria-label={s.detail}>
      <span class="stagebar-segs" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <span class={`stagebar-seg${i <= s.position ? ' is-done' : ''}${s.stage === 'ready_to_deliver' && i === 5 ? ' is-ready' : ''}`} />
        ))}
      </span>
      <span class="stagebar-label" aria-hidden="true">
        {s.label}
        {s.tag ? <span class="stagebar-tag"> · {s.tag}</span> : null}
      </span>
      {p.compact ? null : (
        <span class="small muted stagebar-detail" aria-hidden="true">
          {s.detail.replace(`${s.label} · `, '')}
        </span>
      )}
    </span>
  );
}

/** File-style names (Tips_8_Hallbarhet_i_kylen_MALL) may wrap after each underscore instead of mid-word. */
const breakable = (name: string) => name.split('_').flatMap((part, i, all) => (i < all.length - 1 ? [`${part}_`, <wbr />] : [part]));

export const dueText = (d: string | null, lang: Lang = 'en') => (d ? TX[lang].due(lang === 'sv' ? ddmSv(d) : ddm(d)) : '–');
/** The list's Deadline column: the date alone, the column already says what it is (owner 2026-11-03). */
export const dayText = (d: string | null, lang: Lang = 'en') => (d ? (lang === 'sv' ? ddmSv(d) : ddm(d)) : '–');

/** The five stages in order, with what each means for the project (the page's journey and the list's legend). */
/** The texts are TX.journey, in the same order. */
const JOURNEY = [{ stage: 'planning' }, { stage: 'editing' }, { stage: 'internal_review' }, { stage: 'with_customer' }, { stage: 'delivered' }];
const journeyAt = (stage: string) => (stage === 'ready_to_deliver' ? 4 : JOURNEY.findIndex((j) => j.stage === stage));

/** The quick views that are a stage carry its colour (owner 2026-11-01): the views are the legend, one chip row. */
const VIEW_DOT: Record<string, string> = { planning: 'planning', editing: 'editing', review: 'internal_review', client: 'with_customer' };

/**
 * Where the project is: five stages in one slim row, done ones ticked. Each stage says how many of its tasks are done and
 * opens them under the row on a click (owner 2026-10-31, stageTasks.tsx). `?step=editing` opens a stage (My work links).
 */
function Journey(p: { stage: StageView; api: Api; projectId: string; rev: string }) {
  const lang = useLang();
  const t = TX[lang];
  const at = journeyAt(p.stage.stage);
  const done = p.stage.stage === 'delivered';
  const { list, act } = useStageTasks(p.api, p.projectId, p.rev);
  const [open, setOpen] = useState<TaskStage | null>(() => {
    const q = new URLSearchParams(location.search).get('step');
    return JOURNEY.some((j) => j.stage === q) ? (q as TaskStage) : null;
  });
  if (at < 0) return null;
  const tasksOf = (stage: string) => list?.stages?.find((s) => s.stage === stage);
  const panel = (i: number) => {
    const s = tasksOf(JOURNEY[i]!.stage);
    if (!list || !s || open !== s.stage) return null;
    return (
      <div class="journey-tasks">
        <StageTaskPanel stage={s} list={list} act={act} title={i === 4 && p.stage.stage === 'ready_to_deliver' ? t.readyToDeliver : t.journey[i]!.title} text={t.journey[i]!.text} />
      </div>
    );
  };
  const openAt = JOURNEY.findIndex((j) => j.stage === open);
  return (
    // one slim row (owner 2026-11-01): the steps with the explanation on hover, so the videos come up in view
    <section class="journey-slim" aria-label={`${t.whereProject} · ${t.stageOf(at + 1, JOURNEY.length)}`} data-tour="journey">
      <ol class={`journey st-${p.stage.stage}`}>
        {JOURNEY.map((j, i) => {
          const state = i < at || (done && i === at) ? 'done' : i === at ? 'now' : 'todo';
          const s = tasksOf(j.stage);
          const isOpen = open === j.stage;
          const count = stageCount(s, lang);
          return (
            <li class={`journey-step is-${state}${isOpen ? ' is-open' : ''}`} aria-current={state === 'now' ? 'step' : undefined} title={t.journey[i]!.text}>
              <button type="button" class="journey-click" aria-expanded={s ? isOpen : undefined} disabled={!s} onClick={() => setOpen(isOpen ? null : (j.stage as TaskStage))}>
                <span class={`journey-bar${s && s.total && state !== 'done' ? ' has-fill' : ''}`} aria-hidden="true">
                  {s && s.total && state !== 'done' ? <i style={`width:${Math.round((s.done / s.total) * 100)}%`} /> : null}
                </span>
                <span class="journey-head">
                  <span class="journey-mark" aria-hidden="true">
                    {state === 'done' ? '✓' : i + 1}
                  </span>
                  {i === 4 && p.stage.stage === 'ready_to_deliver' ? t.readyToDeliver : t.journey[i]!.title}
                </span>
                {count ? (
                  <span class="journey-count">
                    <span class="jc-long">{count}</span>
                    <span class="jc-short">{stageCount(s, lang, true)}</span>
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
      {p.stage.detail !== p.stage.label ? <p class="small muted journey-mix">{p.stage.detail}</p> : null}
      {openAt >= 0 ? panel(openAt) : null}
    </section>
  );
}

/** Undo in the toast (UX-990). */
export function announce(api: Api, r: Change, reload: (p: ProjectDetail) => void, lang: Lang = 'en') {
  toast({
    text: r.text,
    undo: r.undoId
      ? async () => {
          try {
            const u = await api.post<Change>(`/api/projects/undo/${r.undoId}`);
            reload(u.project);
            toast({ text: TX[lang].undone });
          } catch (e) {
            toast({ text: errText(e) });
          }
        }
      : undefined
  });
}

/* ---------------- list (UX-114) ---------------- */

const ROLE_ORDER = ['producer', 'cd', 'editor', 'camera'];
/** Producer first, then CD, editors and camera: the order the list's balls are drawn in. */
/** The person columns on the list: producer, editing, camera (owner 2026-10-21). */
const ROLE_COLS = [
  ['producer', 'colProducer'],
  ['editor', 'colEditing'],
  ['camera', 'colCamera']
] as const;

export const byRole = <T extends { role: string }>(people: T[]) => [...people].sort((a, b) => (ROLE_ORDER.indexOf(a.role) + 1 || 9) - (ROLE_ORDER.indexOf(b.role) + 1 || 9));

type ListData = { projects: ProjectSummary[]; canCreate?: boolean; from?: string; to?: string };
const MY_ROLES = ['producer', 'editor', 'camera'] as const;
// the crew's Mine/All and the rows they unfolded are remembered on the device (owner 2026-10-20, 2026-11-03)
const SCOPE_KEY = 'kff.board.scope';
const OPEN_KEY = 'kff.projects.open';
const readScope = () => {
  try {
    return localStorage.getItem(SCOPE_KEY) === 'all' ? 'all' : 'mine';
  } catch {
    return 'mine';
  }
};
const saveScope = (s: 'mine' | 'all') => {
  try {
    localStorage.setItem(SCOPE_KEY, s);
  } catch {
    /* private mode: the choice lasts until the page closes */
  }
};
const readOpen = () => {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(OPEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set<string>();
  }
};
const saveOpen = (ids: Set<string>) => {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    /* private mode */
  }
};

const listPath = (mine: boolean, f: ProjectFilters) => {
  const q = new URLSearchParams();
  if (mine) q.set('mine', '1');
  if (f.from) q.set('from', f.from);
  if (f.to) q.set('to', f.to);
  const qs = q.toString();
  return `/api/projects${qs ? `?${qs}` : ''}`;
};

/** `archive`: the Archive view (every project, the Hub's and Monday's history); KFF employees only. */
/**
 * The projects list, ONE for every role (owner 2026-11-03: "en lista och komponent"): the same columns, quick filters,
 * the videos under the arrow and the ⋯ shortcuts for producers, CDs, admins, editors, camera and freelancers. Roles
 * differ only in what the row lets them change (`allow`) and, for freelancers, which projects open.
 * `crew`: the rows come from /api/crew/board (editors, camera, freelancers on the link), the rest from /api/projects.
 */
export function ProjectsList(p: { api: Api; archive?: boolean; crew?: { openAll: boolean; open: (row: ProjectSummary) => void } }) {
  const lang = useLang();
  const t = TX[lang];
  const fl = filterLabels(lang);
  const crew = p.crew;
  const [mine, setMine] = useState(() => {
    const q = new URLSearchParams(location.search).get('mine');
    return q === null ? (crew ? readScope() !== 'all' : true) : q !== '0';
  });
  // the Archive is read from the server only while it is open (owner 2026-11-02: no extra load on every page view)
  const [archive, setArchive] = useState(() => Boolean(p.archive) && new URLSearchParams(location.search).get('view') === 'archive');
  const [reopen, setReopen] = useState<ArchiveRow | null>(null);
  const [f, setF] = useState<ProjectFilters>(() => readFilters(location.search));
  const path = crew ? `/api/crew/board?scope=${mine ? 'mine' : 'all'}` : listPath(mine, f);
  const [v, setV] = useState<ListData | null>(() => peek(p.api, path) ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  // editing and camera, and booking a shoot, straight from a row (owner 2026-10-31)
  const [teamFor, setTeamFor] = useState<{ id: string; role: string; label: string; current: string[]; at: HTMLElement } | null>(null);
  const [bookFor, setBookFor] = useState<string | null>(null);
  // the videos under a row's arrow; what I opened stays open (owner 2026-11-03)
  const [openIds, setOpenIds] = useState<Set<string>>(readOpen);
  const [beside, setBeside] = useState<ProjectSummary | null>(null);
  const [sending, setSending] = useState<{ row: ProjectSummary; video: ListVideo } | null>(null);
  const reload = () => p.api.get<ListData>(path).then(setV, () => undefined);
  // Advanced filters start open when a shared link carries any of them
  const [more, setMore] = useState(() => advancedCount(readFilters(location.search)) > 0);
  // Mine is the default, but someone on no project (an admin, a new hire) lands on All instead of an empty page
  const firstLoad = useRef(new URLSearchParams(location.search).get('mine') === null);
  const [autoAll, setAutoAll] = useState(false);
  useEffect(() => {
    if (archive) return;
    // the list seen last time for these filters shows at once and is refreshed underneath
    setV(peek<ListData>(p.api, path) ?? null);
    p.api.get<ListData>(path).then((r) => {
      const fallBack = firstLoad.current && mine && !r.projects.length;
      firstLoad.current = false;
      if (fallBack) {
        setAutoAll(true);
        setMine(false);
      } else setV(r);
    }, setErr);
  }, [path, archive]);
  // the filters live in the URL so a filtered list can be bookmarked or shared
  useEffect(() => {
    const s = new URLSearchParams(writeFilters(location.search, f));
    if (mine) s.delete('mine');
    else s.set('mine', '0');
    if (archive) s.set('view', 'archive');
    else s.delete('view');
    const q = s.toString();
    if (`?${q}` !== location.search && (q || location.search)) history.replaceState(history.state, '', `${location.pathname}${q ? `?${q}` : ''}`);
    if (crew) saveScope(mine ? 'mine' : 'all');
  }, [f, mine, archive]);
  const set = (patch: Partial<ProjectFilters>) => setF((cur) => ({ ...cur, ...patch }));
  const toggle = (id: string) => {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenIds(next);
    saveOpen(next);
  };
  if (err) return <LoadError error={err} />;
  const today = todayKey();
  const all = v?.projects ?? [];
  const done = all.filter((x) => isDone(x) && !x.hidden).length;
  const nHidden = all.filter((x) => x.hidden).length;
  // "Din tur" with its count, as on My work
  const nTurn = all.filter((x) => !x.hidden && !isDone(x) && (x.myTurn ?? x.next.mine)).length;
  // "Min roll" only for someone with more than one of the roles on their own projects (owner 2026-11-02)
  const myRoles = MY_ROLES.filter((r) => all.some((x) => x.mine && hasMyRole(x, r)));
  const fx = { ...f, role: mine && myRoles.length > 1 ? f.role : ('' as const), hidden: f.hidden && nHidden > 0 };
  const shown = all.filter((x) => matches(x, fx, today));
  const narrowed = activeCount(fx) > 0;
  const opts = filterOptions(f.done ? all : all.filter((x) => !isDone(x)));
  const adv = advancedCount(f);
  // upcoming months first, the current month, then past months below (owner decision 2026-10-14)
  const months = [...new Set(shown.map((x) => x.deliveryMonth))].sort().reverse();
  const now = thisMonth();
  // the current month is open; the others are folded with their count (or the first month when none is current).
  // With a filter on, every month with a match is open so nothing found is hidden.
  const open = months.includes(now) ? now : months.find((m) => m < now) ?? months[months.length - 1];
  const foldKey = `${mine}-${f.done}-${narrowed ? JSON.stringify(fx) : ''}`;
  // older API versions leave out people; the Team column then hides
  const hasPeople = all.some((x) => x.people);
  const hasShoots = all.some((x) => x.shoot !== undefined);
  const canOpen = (x: ProjectSummary) => !crew || crew.openAll || x.mine;
  const openRow = (x: ProjectSummary) => (crew ? crew.open(x) : navigate(`/projects/${x.id}`));
  // "Din tur" opens the videos of the projects it shows: what is mine is one look away
  const isOpen = (x: ProjectSummary) => openIds.has(x.id) || (f.wait === 'turn' && !!(x.myTurn ?? x.next.mine));
  // straight on the video, as editing: no dialog (owner 2026-11-02, a shortcut)
  const startTimer = async (x: ProjectSummary, video: ListVideo) => {
    try {
      await p.api.post('/api/time/timer/start', { projectId: x.id, videoId: video.id, kind: 'editing' });
      timerChanged();
      toast({ text: t.timerOn(video.name) });
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  // the video's next step, as the project page's one button: sending for internal review picks the file too
  const forward = async (x: ProjectSummary, video: ListVideo) => {
    if (!video.forward) return;
    if (video.forward.key === 'submit_internal') return setSending({ row: x, video });
    try {
      const r = await p.api.post<Change>(`/api/projects/${x.id}/videos/${video.id}/command`, { command: video.forward.key });
      announce(p.api, r, () => void reload(), lang);
    } catch (e) {
      toast({ text: errText(e) });
    }
    void reload();
  };
  const hideRow = async (x: ProjectSummary, hidden: boolean) => {
    try {
      await setProjectHidden(p.api, x.id, hidden);
      toast({ text: hidden ? t.hiddenNow(x.name) : t.shownNow(x.name), undo: async () => (await setProjectHidden(p.api, x.id, !hidden), void reload()) });
    } catch (e) {
      toast({ text: errText(e) });
    }
    void reload();
  };
  const roleBalls = (x: ProjectSummary, role: string) => (x.people ?? []).filter((m) => (m.roles ?? [m.role]).includes(role));
  return (
    <div class="screen">
      <PageHead
        title={t.projects}
        sub={
          archive
            ? t.archiveSub
            : !v
              ? undefined
              : crew || !v.from || !v.to
                ? t.crewSub(all.filter((x) => !x.hidden).length, mine)
                : t.listSub(all.length - done - nHidden, monthLabel(v.from, lang), monthLabel(v.to, lang))
        }
        action={
          v?.canCreate ? (
            <button type="button" class="btn btn-primary" data-tour="new-project" onClick={() => setCreating(true)}>
              {t.newProject}
            </button>
          ) : null
        }
      />
      <div class="pf" role="search" aria-label={t.filterProjects}>
        <div class="cluster">
          <div class="seg" role="group" aria-label={t.show} data-tour="project-scope">
            {[true, false].map((m) => (
              <button type="button" class={`seg-btn${!archive && mine === m ? ' is-on' : ''}`} aria-pressed={!archive && mine === m} onClick={() => (setAutoAll(false), setArchive(false), setMine(m))}>
                {m ? t.mine : t.all}
              </button>
            ))}
            {p.archive ? (
              <button type="button" class={`seg-btn${archive ? ' is-on' : ''}`} aria-pressed={archive} onClick={() => setArchive(true)}>
                {t.archive}
              </button>
            ) : null}
          </div>
          {!archive && mine && myRoles.length > 1 ? (
            <div class="seg" role="group" aria-label={t.myRole}>
              {myRoles.map((r) => (
                <button type="button" class={`seg-btn${fx.role === r ? ' is-on' : ''}`} aria-pressed={fx.role === r} onClick={() => set({ role: fx.role === r ? '' : r })}>
                  {t.roleName[r]}
                </button>
              ))}
            </div>
          ) : null}
          {archive ? null : (
            <>
              <input type="search" class="input pf-search" placeholder={t.searchPlaceholder} aria-label={t.searchLabel} value={f.q} onInput={(e) => set({ q: val(e) })} />
              <button type="button" class={`btn pf-toggle${adv ? ' is-set' : ''}`} aria-expanded={more} aria-controls="pf-adv" onClick={() => setMore(!more)}>
                {t.advanced}
                {adv ? ` (${adv})` : ''}
              </button>
            </>
          )}
        </div>
        {archive ? null : (
          <>
            {autoAll && !mine ? <p class="small muted pf-note">{t.autoAll}</p> : null}
            <div class="pf-views" role="group" aria-label={t.quickViews} data-tour="project-views">
              {VIEWS.map((view) => {
                const on = viewOn(f, view);
                return (
                  <button type="button" class="chip" aria-pressed={on} onClick={() => setF(toggleView(f, view))}>
                    {VIEW_DOT[view.key] ? (
                      <span class={`st-${VIEW_DOT[view.key]} pf-dot`}>
                        <span class="st-dot" aria-hidden="true" />
                      </span>
                    ) : null}
                    {fl.view(view)}
                    {view.key === 'turn' && v ? ` (${nTurn})` : ''}
                  </button>
                );
              })}
              {nHidden ? (
                <button type="button" class="chip" aria-pressed={fx.hidden} onClick={() => set({ hidden: !f.hidden })}>
                  {t.hiddenView(nHidden)}
                </button>
              ) : null}
            </div>
            {more ? (
              <div class="pf-row pf-adv" id="pf-adv">
                <FilterSelect id="pf-stage" label={t.stage} value={f.stage} onChange={(x) => set({ stage: x })} options={fl.stages} />
                <FilterSelect id="pf-client" label={t.client} value={f.client} onChange={(x) => set({ client: x })} options={opts.clients.map((c) => [c.id, `${c.name} (${c.n})`])} />
                {opts.people.length ? (
                  <FilterSelect id="pf-person" label={t.person} value={f.person} onChange={(x) => set({ person: x })} options={opts.people.map((c) => [c.id, `${c.name} (${c.n})`])} />
                ) : null}
                <FilterSelect id="pf-due" label={t.deadline} value={f.due} onChange={(x) => set({ due: x as DueFilter })} options={fl.due} />
                <FilterSelect id="pf-wait" label={t.waitingOn} value={f.wait} onChange={(x) => set({ wait: x as WaitFilter })} options={fl.wait} />
                <FilterSelect id="pf-src" label={t.source} value={f.src} onChange={(x) => set({ src: x as SourceFilter })} options={fl.source} />
                {/* the crew's list is running work only, so the delivery window is the producers' */}
                {crew ? null : (
                  <>
                    <div class={`field pf-field${f.from ? ' is-set' : ''}`}>
                      <label class="label small" for="pf-from">
                        {t.deliveryFrom}
                      </label>
                      <input id="pf-from" type="month" class="input" value={f.from || v?.from || ''} onChange={(e) => set({ from: val(e) })} />
                    </div>
                    <div class={`field pf-field${f.to ? ' is-set' : ''}`}>
                      <label class="label small" for="pf-to">
                        {t.deliveryTo}
                      </label>
                      <input id="pf-to" type="month" class="input" value={f.to || v?.to || ''} onChange={(e) => set({ to: val(e) })} />
                    </div>
                  </>
                )}
              </div>
            ) : null}
            <div class="cluster pf-foot">
              {done ? (
                <label class="small">
                  <input type="checkbox" checked={f.done} onChange={(e) => set({ done: (e.target as HTMLInputElement).checked })} /> {t.showDone(done)}
                </label>
              ) : null}
              {v && narrowed ? (
                <>
                  <span class="small muted" aria-live="polite">
                    {t.showing(shown.length, all.filter((x) => !x.hidden).length - (f.done ? 0 : done))}
                  </span>
                  <button type="button" class="btn btn-ghost btn-sm" onClick={() => setF({ ...NO_FILTERS, done: f.done })}>
                    {t.clearFilters}
                  </button>
                </>
              ) : null}
              {p.archive && !mine ? (
                <button type="button" class="btn btn-ghost btn-sm" title={t.olderInArchive} onClick={() => setArchive(true)}>
                  {t.openArchive}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
      {archive ? (
        <ProjectArchive api={p.api} onReopen={setReopen} />
      ) : !v ? (
        <Loading />
      ) : !shown.length ? (
        <div class="empty">
          {narrowed ? (
            <>
              <strong>{t.noMatch}</strong>
              <span class="muted">{mine ? t.noMatchMine : t.noMatchAll}</span>
            </>
          ) : (
            <>
              <strong>{done ? t.nothingOpen : mine ? t.noneOfYours : t.noneYet}</strong>
              <span class="muted">{done ? t.allDone : mine ? t.switchAll : t.mondayStill}</span>
            </>
          )}
        </div>
      ) : (
        months.map((m) => {
          const rows = shown.filter((x) => x.deliveryMonth === m);
          return (
            <details class="blk fold" open={narrowed || m === open} key={`${m}-${foldKey}`} data-tour={m === open ? 'month' : undefined}>
              <summary>
                <span class="blk-title">{cap(monthLabel(m, lang))}</span> <span class="fold-n">{rows.length}</span>
                {m === now ? <span class="month-tag">{t.thisMonth}</span> : m < now ? <span class="month-tag is-past">{t.past}</span> : null}
              </summary>
              <div class="tbl-wrap" role="region" aria-label={t.projectsIn(monthLabel(m, lang))} tabIndex={0}>
                <table class="tbl tbl--rows plist">
                  <thead>
                    <tr>
                      <th scope="col">{t.project}</th>
                      <th scope="col">{t.stage}</th>
                      <th scope="col">{t.nextStep}</th>
                      <th scope="col">{t.deadline}</th>
                      {hasShoots ? (
                        <th scope="col" class="pl-shoot">
                          {t.colShoot}
                        </th>
                      ) : null}
                      {hasPeople
                        ? ROLE_COLS.map(([, col]) => (
                            <th scope="col" class="pl-role">
                              {t[col]}
                            </th>
                          ))
                        : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.flatMap((x) => {
                      const videos = x.videoList ?? [];
                      const unfolded = isOpen(x) && videos.length > 0;
                      const opens = canOpen(x);
                      const canBook = !!x.allow?.book && (x.productionType === 'shoot' || !x.productionType) && !isDone(x);
                      const menu = opens
                        ? [
                            { label: t.readBrief, run: () => navigate(`/projects/${x.id}/brief`) },
                            { label: openBesideLabel(lang), run: () => setBeside(x) },
                            ...(x.mine && !isDone(x) ? [{ label: PT[lang].menu, run: () => openPlan({ projectId: x.id }) }] : []),
                            ...(canBook ? [{ label: t.bookShootOn(x.name), run: () => setBookFor(x.id) }] : []),
                            ...(x.mine && x.hidden !== undefined ? [{ label: x.hidden ? t.showInMyWork : t.hideInMyWork, run: () => void hideRow(x, !x.hidden) }] : [])
                          ]
                        : [];
                      return [
                        <tr key={x.id} class={`pl-row${opens ? ' row-link' : ''}${crew && x.mine ? ' is-mine' : ''}`} onClick={opens ? () => openRow(x) : undefined}>
                          <th scope="row" class="pl-c-name">
                            <div class="pl-head">
                              {videos.length ? (
                                <button
                                  type="button"
                                  class="pl-caret"
                                  aria-expanded={unfolded}
                                  aria-label={t.openVideos(x.name)}
                                  title={t.videosN(videos.length)}
                                  onClick={(e) => (e.stopPropagation(), toggle(x.id))}
                                >
                                  <span aria-hidden="true">{unfolded ? '▾' : '▸'}</span>
                                </button>
                              ) : (
                                <span class="pl-caret" aria-hidden="true" />
                              )}
                              {/* the project name and the client under it, plain text in one colour (owner 2026-10-31) */}
                              {opens ? (
                                <a class="pl-name" href={`/projects/${x.id}`} onClick={(e) => (e.preventDefault(), e.stopPropagation(), openRow(x))} title={x.fromMonday ? t.fromMonday(x.code) : x.code}>
                                  <span class="pl-project">{x.name}</span>
                                  <span class="pl-client">{x.client.name}</span>
                                </a>
                              ) : (
                                <span class="pl-name" title={t.notOn}>
                                  <span class="pl-project">{x.name}</span>
                                  <span class="pl-client">{x.client.name}</span>
                                </span>
                              )}
                              {x.isTest ? <span class="tag pl-test">{t.testTag}</span> : null}
                              {/* your planned time on it; "Ingen tid planerad" only while it is your turn (owner 2026-11-03) */}
                              {x.myPlan ? (
                                <span class="pt-label">{PT[lang].plannedLabel(h(x.myPlan.hours), daysShort(x.myPlan.days, lang))}</span>
                              ) : x.myPlan === null && x.mine && (x.myTurn ?? x.next.mine) && !isDone(x) ? (
                                <span class="pt-label pt-label--none">{PT[lang].nonePlanned}</span>
                              ) : null}
                              {menu.length ? (
                                <span class="pl-more" onClick={(e) => e.stopPropagation()}>
                                  <RowMenu label={t.moreFor(x.name)} items={menu} />
                                </span>
                              ) : null}
                            </div>
                          </th>
                          <td class="pl-c-stage">
                            <StageBar stage={x.stage} compact />
                          </td>
                          <td class="pl-c-next">{x.next.mine ? <strong>{x.next.phrase}</strong> : x.next.phrase}</td>
                          <td class={`num pl-c-due${x.nextDeadline && x.nextDeadline < today && !isDone(x) ? ' is-overdue' : ''}${x.nextDeadline ? '' : ' is-none'}`}>{dayText(x.nextDeadline, lang)}</td>
                          {hasShoots ? (
                            <td class="pl-shoot">
                              <ShootCell x={x} onBook={x.allow?.book ? () => setBookFor(x.id) : undefined} />
                            </td>
                          ) : null}
                          {/* a column per role; the CD shows on the project page only (owner 2026-10-21) */}
                          {hasPeople
                            ? ROLE_COLS.map(([role, col]) => {
                                const has = roleBalls(x, role);
                                const people = <RolePeople people={has.map((m) => ({ name: m.name, role: t[col] }))} />;
                                // the producer is changed on the project page; editing and camera right here (owner 2026-10-31)
                                const settable = role !== 'producer' && x.allow?.team;
                                return (
                                  <td class="pl-role">
                                    {settable ? (
                                      <button
                                        type="button"
                                        class={`pl-set${has.length ? '' : ' is-empty'}`}
                                        aria-label={t.setRoleOn(t[col], x.name)}
                                        title={t.setRoleOn(t[col], x.name)}
                                        onClick={(e) => (e.stopPropagation(), setTeamFor({ id: x.id, role, label: t[col], current: has.map((m) => m.id), at: e.currentTarget as HTMLElement }))}
                                      >
                                        {has.length ? people : <AddBall size="sm" />}
                                      </button>
                                    ) : (
                                      people
                                    )}
                                  </td>
                                );
                              })
                            : null}
                        </tr>,
                        ...(unfolded
                          ? videos.map((video) => {
                              const items = opens
                                ? [
                                    ...(video.forward ? [{ label: video.forward.key === 'submit_internal' ? t.uploadDraft : video.forward.label, run: () => void forward(x, video) }] : []),
                                    { label: t.openReviewOf, run: () => navigate(`/projects/${x.id}/review/${video.id}`) },
                                    ...(video.mine ? [{ label: t.startTimer, run: () => void startTimer(x, video) }] : [])
                                  ]
                                : [];
                              return (
                                <tr key={video.id} class={`pl-video${video.mine ? ' is-mine' : ''}`}>
                                  <th scope="row" class="pl-c-name">
                                    <div class="pl-head">
                                      <span class="pl-vname">{video.name}</span>
                                      {items.length ? (
                                        <span class="pl-more">
                                          <RowMenu label={t.moreFor(video.name)} items={items} />
                                        </span>
                                      ) : null}
                                    </div>
                                  </th>
                                  <td class="pl-c-stage">
                                    <StageBar stage={video.stage} compact />
                                  </td>
                                  <td class="pl-c-next">{video.next.mine ? <strong>{video.next.phrase}</strong> : video.next.phrase}</td>
                                  <td class={`num pl-c-due${video.due && video.due < today && video.stage.stage !== 'delivered' ? ' is-overdue' : ''}${video.due ? '' : ' is-none'}`}>{dayText(video.due, lang)}</td>
                                  {hasShoots ? <td class="pl-shoot" /> : null}
                                  {hasPeople
                                    ? ROLE_COLS.map(([role, col]) => (
                                        <td class="pl-role">{role === 'editor' ? <RolePeople people={video.editor ? [{ name: video.editor, role: t[col] }] : []} /> : null}</td>
                                      ))
                                    : null}
                                </tr>
                              );
                            })
                          : [])
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          );
        })
      )}
      {creating ? <NewProjectDialog api={p.api} onClose={() => setCreating(false)} /> : null}
      {reopen ? <NewProjectDialog api={p.api} from={reopen} onClose={() => setReopen(null)} /> : null}
      {teamFor ? (
        <RolePicker
          api={p.api}
          projectId={teamFor.id}
          role={teamFor.role}
          label={teamFor.label}
          current={teamFor.current}
          at={teamFor.at}
          onClose={() => setTeamFor(null)}
          onSaved={(r) => {
            announce(p.api, r, () => void reload(), lang);
            void reload();
          }}
        />
      ) : null}
      {bookFor ? (
        <BookShoot
          api={p.api}
          projectId={bookFor}
          onClose={() => setBookFor(null)}
          onDone={(r) => {
            toast({ text: r.text });
            void reload();
          }}
        />
      ) : null}
      {sending ? (
        <SendForReview
          api={p.api}
          projectId={sending.row.id}
          videoId={sending.video.id}
          videoName={sending.video.name}
          fallback={async () => {
            try {
              await p.api.post(`/api/projects/${sending.row.id}/videos/${sending.video.id}/command`, { command: 'submit_internal' });
            } catch (e) {
              toast({ text: errText(e) });
            }
            void reload();
          }}
          onDone={async () => void reload()}
          onClose={() => setSending(null)}
        />
      ) : null}
      {beside ? <OpenBeside api={p.api} from={{ id: beside.id, name: beside.name }} mineOnly={!!crew && !crew.openAll} onClose={() => setBeside(null)} /> : null}
    </div>
  );
}

/** The shoot column: the next shoot (Monday's day in italics), else the last one, muted; Book when nothing is coming. */
export function ShootCell(p: { x: ProjectSummary; onBook?: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const s = p.x.shoot;
  const day = s ? (lang === 'sv' ? ddmSv(s.date) : ddm(s.date)) : null;
  const canBook = !!p.onBook && p.x.allow?.book && (!s || s.past) && (p.x.productionType === 'shoot' || !p.x.productionType) && !isDone(p.x);
  return (
    <span class="pl-shoot-in">
      {s ? (
        <span class={`num${s.past ? ' muted' : ''}${s.monday ? ' pl-shoot-monday' : ''}${s.mondaySays ? ' warn-text' : ''}`} title={s.mondaySays ? t.mondaySays(s.mondaySays) : s.monday ? t.shootFromMonday : s.past ? t.shootDone : undefined}>
          {day}
        </span>
      ) : canBook ? null : (
        <span class="muted">–</span>
      )}
      {canBook ? (
        <button type="button" class="btn btn-ghost btn-sm pl-book" aria-label={t.bookShootOn(p.x.name)} onClick={(e) => (e.stopPropagation(), p.onBook?.())}>
          + {t.bookShootShort}
        </button>
      ) : null}
    </span>
  );
}

/** Who has a role on a project: the shared search list, saved at once; the first one is the primary. */
function RolePicker(p: { api: Api; projectId: string; role: string; label: string; current: string[]; at: HTMLElement; onClose: () => void; onSaved: (r: Change) => void }) {
  const t = TX[useLang()];
  const o = useOptions(p.api);
  return (
    <PersonPicker
      label={p.label}
      people={o ? (o.people[p.role as keyof Options['people']] ?? []) : null}
      current={p.current}
      at={p.at}
      empty={t.nobodyHasRole(p.label)}
      onClose={p.onClose}
      onPick={async (ids) => p.onSaved(await p.api.put<Change>(`/api/projects/${p.projectId}/team`, { role: p.role, personIds: ids }))}
    />
  );
}

function FilterSelect(p: { id: string; label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  const t = TX[useLang()];
  return (
    <div class={`field pf-field${p.value ? ' is-set' : ''}`}>
      <label class="label small" for={p.id}>
        {p.label}
      </label>
      <select id={p.id} class="input" value={p.value} onChange={(e) => p.onChange(val(e))}>
        <option value="">{t.any}</option>
        {p.options.map(([k, t]) => (
          <option value={k}>{t}</option>
        ))}
        {/* a value from a shared link that is not in this list (another window, say) still shows */}
        {p.value && !p.options.some(([k]) => k === p.value) ? <option value={p.value}>{t.notInList}</option> : null}
      </select>
    </div>
  );
}

/* ---------------- New project ---------------- */

function useOptions(api: Api) {
  const [o, setO] = useState<Options | null>(null);
  useEffect(() => {
    api.get<Options>('/api/projects/options').then(setO, () => setO(null));
  }, []);
  return o;
}

const todayKey = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
const thisMonth = () => todayKey().slice(0, 7);

interface Suggestion {
  producerId: string | null;
  productionType: string | null;
  creativePackage: string | null;
  videos: number | null;
  needsFinalFormats: boolean | null;
}

/** `from`: a Monday history project opened in the Hub again (owner 2026-11-02: feedback on an old project). */
export function NewProjectDialog(p: { api: Api; onClose: () => void; clientId?: string; from?: ArchiveRow }) {
  const lang = useLang();
  const t = TX[lang];
  const o = useOptions(p.api);
  const [clientId, setClientId] = useState(p.clientId ?? p.from?.clientId ?? '');
  const [newName, setNewName] = useState('');
  const [name, setName] = useState(p.from?.name.slice(0, 160) ?? '');
  const [month, setMonth] = useState(thisMonth());
  const [type, setType] = useState('shoot');
  const [pkg, setPkg] = useState('');
  // new feedback on an old project is usually one video's changes
  const [videos, setVideos] = useState(p.from ? '1' : '');
  const [finals, setFinals] = useState(true);
  const [isTest, setIsTest] = useState(false);
  const [english, setEnglish] = useState(false);
  const [producerId, setProducerId] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // fields the person changed themselves keep their value when the client (and with it the suggestion) changes
  const touched = useRef(new Set<string>(p.from ? ['videos'] : []));
  const touch = (k: string) => touched.current.add(k);
  useEffect(() => {
    if (o && !producerId && o.people.producer.some((x) => x.id === o.me.id)) setProducerId(o.me.id);
  }, [o]);
  useEffect(() => {
    if (!clientId || clientId === '__new') return;
    let live = true;
    p.api.get<Suggestion>(`/api/projects/suggest?clientId=${encodeURIComponent(clientId)}`).then(
      (x) => {
        if (!live) return;
        const keep = (k: string) => touched.current.has(k);
        if (x.producerId && !keep('producer')) setProducerId(x.producerId);
        if (x.productionType && !keep('type')) setType(x.productionType);
        if (x.creativePackage && !keep('pkg')) setPkg(x.creativePackage);
        if (x.videos !== null && x.videos > 0 && !keep('videos')) setVideos(String(x.videos));
        if (x.needsFinalFormats !== null && !keep('finals')) setFinals(x.needsFinalFormats);
      },
      () => undefined // no suggestion: the defaults stay
    );
    return () => {
      live = false;
    };
  }, [clientId]);
  const isNew = clientId === '__new';
  const submit = async () => {
    setBusy(true);
    setErrs({});
    try {
      const r = await p.api.post<{ project: ProjectDetail }>('/api/projects', {
        ...(isNew ? { newClient: { name: newName, ...(english ? { language: 'en' } : {}) } } : { clientId }),
        name,
        deliveryMonth: month,
        productionType: type,
        creativePackage: pkg || null,
        videos: videos.trim() ? Number(videos) : 0,
        needsFinalFormats: finals,
        ...(isTest ? { isTest: true } : {}),
        ...(producerId ? { producerId } : {}),
        ...(p.from ? { fromHistory: p.from.id } : {})
      });
      toast({ text: t.created(r.project.name) });
      navigate(`/projects/${r.project.id}`);
    } catch (e) {
      const d = e instanceof ApiError ? (e.details as { errors?: Record<string, string> } | undefined) : undefined;
      setErrs(d?.errors ?? { form: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const err = (k: string) => (errs[k] ? <span class="field-error">{errs[k]}</span> : null);
  const inv = (k: string) => (errs[k] ? { 'aria-invalid': true as const } : {});
  return (
    <Dialog
      title={p.from ? t.reopenTitle : t.newProjectTitle}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy || !o} onClick={() => void submit()}>
            {t.createProject}
          </button>
        </>
      }
    >
      {!o ? (
        <Loading />
      ) : (
        <div class="stack">
          {errs.form ? <ErrorBox error={errs.form} /> : null}
          {p.from ? <p class="small muted">{t.reopenNote(p.from.month ? monthLabel(p.from.month, lang) : '–')}</p> : null}
          <div class="field">
            <label class="label" for="np-client">
              {t.client}
            </label>
            <select id="np-client" class="input input--wide" value={clientId} onChange={(e) => setClientId(val(e))} {...inv('client')}>
              <option value="">{t.choose}</option>
              {o.clients.map((c) => (
                <option value={c.id}>{c.name}</option>
              ))}
              <option value="__new">{t.newClient}</option>
            </select>
            {err('client')}
          </div>
          {isNew ? (
            <div class="field">
              <label class="label" for="np-cname">
                {t.clientName}
              </label>
              <input id="np-cname" class="input input--wide" value={newName} onInput={(e) => setNewName(val(e))} {...inv('clientCode')} />
              {err('clientCode')}
            </div>
          ) : null}
          <div class="field">
            <label class="label" for="np-name">
              {t.projectName}
            </label>
            <input id="np-name" class="input input--wide" value={name} onInput={(e) => setName(val(e))} {...inv('name')} />
            {err('name')}
          </div>
          <div class="field">
            <label class="label" for="np-month">
              {t.deliveryMonth}
            </label>
            <input id="np-month" class="input input--date" type="month" value={month} onInput={(e) => setMonth(val(e))} {...inv('deliveryMonth')} />
            {err('deliveryMonth')}
          </div>
          <MoreOptions id="new-project" open={Boolean(errs.videos || errs.creativePackage || errs.producerId || errs.productionType)}>
            <div class="grid2">
              <div class="field">
                <label class="label" for="np-videos">
                  {t.videos}
                </label>
                <input id="np-videos" class="input" type="number" min={0} max={30} placeholder={t.videosUnknown} value={videos} onInput={(e) => (touch('videos'), setVideos(val(e)))} aria-describedby="np-videos-hint" {...inv('videos')} />
                <span class="hint" id="np-videos-hint">
                  {t.videosHint}
                </span>
                {err('videos')}
              </div>
              <div class="field">
                <label class="label" for="np-type">
                  {t.productionType}
                </label>
                <select id="np-type" class="input input--wide" value={type} onChange={(e) => (touch('type'), setType(val(e)))}>
                  {o.productionTypes.map((x) => (
                    <option value={x}>{t.type[x] ?? x}</option>
                  ))}
                </select>
              </div>
              <div class="field">
                <label class="label" for="np-pkg">
                  {t.package}
                </label>
                <select id="np-pkg" class="input input--wide" value={pkg} onChange={(e) => (touch('pkg'), setPkg(val(e)))} {...inv('creativePackage')}>
                  <option value="">{t.notSet}</option>
                  {o.packages.map((x) => (
                    <option value={x}>{x}</option>
                  ))}
                </select>
                {err('creativePackage')}
              </div>
              <div class="field">
                <label class="label" for="np-producer">
                  {t.producer}
                </label>
                <select id="np-producer" class="input input--wide" value={producerId} onChange={(e) => (touch('producer'), setProducerId(val(e)))} {...inv('producerId')}>
                  <option value="">{t.nobodyYet}</option>
                  {o.people.producer.map((x) => (
                    <option value={x.id}>{x.name}</option>
                  ))}
                </select>
                {err('producerId')}
              </div>
            </div>
            <label class="check">
              <input type="checkbox" checked={finals} onChange={(e) => (touch('finals'), setFinals((e.target as HTMLInputElement).checked))} /> {t.finalsAfter}
            </label>
            {isNew ? (
              <label class="check">
                <input type="checkbox" checked={english} onChange={(e) => setEnglish((e.target as HTMLInputElement).checked)} /> {t.clientEnglish}
              </label>
            ) : null}
            <label class="check">
              <input type="checkbox" checked={isTest} onChange={(e) => setIsTest((e.target as HTMLInputElement).checked)} aria-describedby="np-test-hint" /> {t.testProject}
            </label>
            <span class="hint" id="np-test-hint">
              {t.testHint}
            </span>
          </MoreOptions>
        </div>
      )}
    </Dialog>
  );
}

/* ---------------- project page (UX-120–122) ---------------- */

export type ProjectTab = 'videos' | 'brief' | 'shoots' | 'actors' | 'notes' | 'files' | 'activity' | 'details';

/** `crew`: editing and camera, employed or on the personal link (owner 2026-10-20/21); the client is not a link. */
export function ProjectPage(p: { api: Api; id: string; tab: ProjectTab; crew?: boolean; link?: boolean; meId?: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<ProjectDetail | null>(() => peek(p.api, `/api/projects/${p.id}`) ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [beside, setBeside] = useState(false);
  useEffect(() => {
    p.api.get<ProjectDetail>(`/api/projects/${p.id}`).then(setV, setErr);
  }, [p.id]);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const run = async (fn: () => Promise<Change>) => {
    try {
      const r = await fn();
      setV(r.project);
      announce(p.api, r, setV, lang);
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  const live = v.videos.filter((x) => x.step !== 'cancelled');
  const delivered = live.filter((x) => x.step === 'delivered').length;
  // the people on the project plan their own time on it (owner 2026-11-03); everyone who sees the project sees the plan
  const canPlan = !v.cancelled && live.some((x) => x.step !== 'delivered') && (v.team.some((r) => r.people.some((x) => x.id === p.meId)) || live.some((x) => x.editor?.id === p.meId));
  return (
    <div class="screen">
      <p>
        <a class="back-link" href="/projects" onClick={go('/projects')}>
          {t.backToProjects}
        </a>
      </p>
      <header class="phead pj-head">
        <div class="pj-top">
          <div class="pj-title">
            <p class="pj-eyebrow">
              {p.crew ? (
                v.client.name
              ) : (
                <a href={`/clients/${v.client.id}`} onClick={go(`/clients/${v.client.id}`)}>
                  {v.client.name}
                </a>
              )}
            </p>
            <h1 class="h1" title={v.code}>
              {v.name}
              {v.isTest ? <span class="tag pl-test">{t.testTag}</span> : null}
            </h1>
            <p class="sub">
              {t.headSub(monthLabel(v.deliveryMonth, lang), live.length, delivered)}
            </p>
          </div>
          <div class="pj-top-side">
            <TeamBar api={p.api} v={v} run={run} />
            {/* Fler val on every tab: open another project beside this one (owner 2026-11-02, for editors) */}
            <ProjectMenu v={v} canPlan={canPlan} onCancel={() => setCancelling(true)} onRemove={() => setRemoving(true)} onBeside={() => setBeside(true)} />
          </div>
        </div>
        <PageTabs />
      </header>
      {v.cancelled ? (
        <CancelledBanner<ProjectDetail> api={p.api} id={v.id} reason={v.cancelled.reason} canAct={Boolean(v.can.cancel)} onResumed={(r) => (setV(r.project), announce(p.api, r, setV, lang))} onRemove={() => setRemoving(true)} />
      ) : null}
      {v.mondayOwned && (p.tab === 'videos' || p.tab === 'details') ? (
        <Callout tone="info" title={t.mondayTitle}>
          {t.mondayBody}
        </Callout>
      ) : null}
      {p.tab === 'videos' && !v.cancelled ? <Journey stage={v.stage} api={p.api} projectId={v.id} rev={v.videos.map((x) => `${x.phase}.${x.step}.${x.editor?.id ?? ''}`).join(',') + v.team.map((r) => r.people.map((x) => x.id).join('+')).join('|')} /> : null}
      {/* the stage and "what happens now" lead the Videos tab; the other tabs open straight on their content */}
      {p.tab === 'videos' && !v.cancelled ? (
      <section class="now-box" aria-labelledby="ns-h" data-tour="now">
        <div>
          <h2 id="ns-h">{t.whatNow}</h2>
          <p class="now-text">
            {v.next.who?.length && !v.next.mine ? <AvatarStack people={v.next.who} max={3} label={t.waitingOn} /> : null}
            <span>{v.next.text}</span>
          </p>
        </div>
        <div class="cluster">
            <NowButton api={p.api} v={v} run={run} />
            {/* "Boka inspelning" while nothing is booked; after that it waits in ⋯ (owner 2026-11-01) */}
            {v.can.edit && v.productionType === 'shoot' && !v.shootBooked ? (
              <a class="btn" href={`/projects/${v.id}/shoots?book=1`} onClick={go(`/projects/${v.id}/shoots?book=1`)}>
                {t.bookShoot}
              </a>
            ) : null}
            {v.can.markShootDone ? (
              <button type="button" class="btn" onClick={() => void run(() => p.api.post<Change>(`/api/projects/${v.id}/shoot-done`))}>
                {t.markShootDone}
              </button>
            ) : null}
            {v.can.edit ? (
              <button type="button" class="btn" onClick={() => setAdding(true)}>
                {t.addVideoMenu}
              </button>
            ) : null}
        </div>
      </section>
      ) : null}
      {p.tab === 'videos' && !v.cancelled ? <ProjectPlan api={p.api} projectId={v.id} canPlan={canPlan} /> : null}
      {p.tab === 'videos' ? (
        <Videos api={p.api} v={v} run={run} />
      ) : p.tab === 'brief' ? (
        <ProjectBriefTab api={p.api} id={v.id} crew={p.crew || p.link} />
      ) : p.tab === 'shoots' ? (
        <ProjectShoots api={p.api} id={v.id} />
      ) : p.tab === 'actors' ? (
        <ProjectActors api={p.api} id={v.id} />
      ) : p.tab === 'notes' ? (
        <ProjectNotes api={p.api} id={v.id} />
      ) : p.tab === 'files' ? (
        <ProjectFiles api={p.api} id={v.id} />
      ) : p.tab === 'activity' ? (
        <ProjectActivity api={p.api} id={v.id} />
      ) : (
        <Details api={p.api} v={v} run={run} crew={p.crew} set={setV} />
      )}
      {adding ? <AddVideoDialog onClose={() => setAdding(false)} onAdd={(name) => run(() => p.api.post<Change>(`/api/projects/${v.id}/videos`, { name }))} /> : null}
      {cancelling ? <CancelProjectDialog<ProjectDetail> api={p.api} id={v.id} name={v.name} hasShoots={Boolean(v.shootBooked)} onClose={() => setCancelling(false)} onDone={(r) => (setV(r.project), announce(p.api, r, setV, lang))} /> : null}
      {beside ? <OpenBeside api={p.api} from={{ id: v.id, name: v.name }} mineOnly={p.link} onClose={() => setBeside(false)} /> : null}
      {removing ? <RemoveProjectDialog api={p.api} id={v.id} onClose={() => setRemoving(false)} onRemoved={() => navigate('/projects')} /> : null}
    </div>
  );
}

/** Fler val on the project (owner 2026-11-02), in the title row: open another project beside it, another shoot, and cancel or remove the project for good. */
function ProjectMenu(p: { v: ProjectDetail; canPlan: boolean; onCancel: () => void; onRemove: () => void; onBeside: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const r = removeTexts[lang];
  const { v } = p;
  const items = [
    ...(p.canPlan ? [{ label: PT[lang].menu, run: () => openPlan({ projectId: v.id }) }] : []),
    { label: openBesideLabel(lang), run: p.onBeside },
    ...(v.can.edit && v.productionType === 'shoot' && v.shootBooked && !v.cancelled ? [{ label: t.bookAnotherShoot, run: () => navigate(`/projects/${v.id}/shoots?book=1`) }] : []),
    ...(v.can.cancel && !v.cancelled ? [{ label: r.cancelMenu, danger: true, run: p.onCancel }] : []),
    ...(v.can.cancel ? [{ label: r.removeMenu, danger: true, run: p.onRemove }] : [])
  ];
  return items.length ? <RowMenu label={t.moreOptions} items={items} /> : null;
}

/** Whether this user may change who has a role here (Monday's projects are locked: their team is set in Monday). */
const canSetRole = (v: ProjectDetail, role: string) => v.can.edit && (role !== 'producer' || v.can.editProducer);

/**
 * The project's people at a glance (owner 2026-11-01): one equal box per role in a card, balls only with name and role on
 * hover. A box opens the same search list as the project list; an empty role shows the dashed ball.
 */
function TeamBar(p: { api: Api; v: ProjectDetail; run: Run }) {
  const t = TX[useLang()];
  const [open, setOpen] = useState<{ role: string; label: string; at: HTMLElement } | null>(null);
  const v = p.v;
  const roles = v.team.filter((r) => r.people.length || canSetRole(v, r.role));
  if (!roles.length) return null;
  return (
    <>
      <ul class="tb" aria-label={t.team} data-tour="team-strip">
        {roles.map((r) => {
          const settable = canSetRole(v, r.role);
          const names = r.people.map((x) => (x.isPrimary && r.people.length > 1 ? `${x.name} (${t.primaryLower})` : x.name)).join(', ');
          const body = (
            <>
              <span class="tb-k">
                <RoleMark role={r.role} />
                {r.label}
              </span>
              <span class="tb-v">
                {r.people.length ? <AvatarStack people={r.people.map((x) => ({ id: x.id, name: x.name, title: `${x.name} · ${r.label}` }))} max={3} label={r.label} /> : settable ? <AddBall /> : <span class="muted">–</span>}
                {settable ? (
                  <span class="tb-car" aria-hidden="true">
                    <Icon name="chevron-down" />
                  </span>
                ) : null}
              </span>
            </>
          );
          return (
            <li class="tb-it">
              {settable ? (
                <button
                  type="button"
                  class={`tb-slot${open?.role === r.role ? ' is-open' : ''}`}
                  aria-label={`${r.label}: ${names || t.nobodyYet}. ${t.changeRole(r.label)}`}
                  aria-expanded={open?.role === r.role}
                  onClick={(e) => setOpen(open?.role === r.role ? null : { role: r.role, label: r.label, at: e.currentTarget as HTMLElement })}
                >
                  {body}
                </button>
              ) : (
                <div class="tb-slot is-static" title={v.mondayOwned ? t.teamInMonday : undefined}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {open ? (
        <RolePicker
          api={p.api}
          projectId={v.id}
          role={open.role}
          label={open.label}
          current={v.team.find((x) => x.role === open.role)?.people.map((x) => x.id) ?? []}
          at={open.at}
          onClose={() => setOpen(null)}
          onSaved={(r) => void p.run(async () => r)}
        />
      ) : null}
    </>
  );
}

/** Editing and camera carry their icon (owner 2026-10-20); the other roles keep a same-size empty spot so labels line up. */
function RoleMark(p: { role: string }) {
  if (p.role !== 'editor' && p.role !== 'camera') return null;
  return (
    <span class={`role-ico role-ico--${p.role}`} aria-hidden="true">
      <Icon name={p.role === 'editor' ? 'scissors' : 'video'} />
    </span>
  );
}

function AddVideoDialog(p: { onClose: () => void; onAdd: (name: string) => Promise<void> }) {
  const t = TX[useLang()];
  const [name, setName] = useState('');
  return (
    <Dialog
      title={t.addVideo}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            disabled={!name.trim()}
            onClick={async () => {
              await p.onAdd(name);
              p.onClose();
            }}
          >
            {t.addVideo}
          </button>
        </>
      }
    >
      <div class="field">
        <label class="label" for="av-name">
          {t.name}
        </label>
        <input id="av-name" class="input input--wide" value={name} placeholder={t.namePlaceholder} onInput={(e) => setName(val(e))} />
      </div>
    </Dialog>
  );
}

export type Run = (fn: () => Promise<Change>) => Promise<void>;

/** UX-019: the order the viewer's own steps come in (as the domain's projectNextStep). */
const NOW_ORDER: string[] = ['producer_review', 'approved', 'internal_review', 'customer_changes', 'internal_changes', 'editing', 'ready_to_edit', 'planned'];

/**
 * "Vad händer nu" with the step's own button (owner 2026-11-01): what waits on me comes first, and one press does it
 * ("Godkänn internt – Huvudfilm"). Nothing of mine: no button, the line says who has it.
 */
function NowButton(p: { api: Api; v: ProjectDetail; run: Run }) {
  const [sending, setSending] = useState<VideoView | null>(null);
  if (!p.v.next.mine) return null;
  const rank = (x: VideoView) => (NOW_ORDER.indexOf(x.step) < 0 ? 99 : NOW_ORDER.indexOf(x.step));
  const top = p.v.videos.filter((x) => x.next.mine && x.forward).sort((a, b) => rank(a) - rank(b) || String(a.next.due ?? '9').localeCompare(String(b.next.due ?? '9')))[0];
  const cmd = top?.commands.find((c) => c.key === top.forward);
  if (!top || !cmd) return null;
  const go = () => (cmd.key === 'submit_internal' ? setSending(top) : void p.run(() => p.api.post<Change>(`/api/projects/${p.v.id}/videos/${top.id}/command`, { command: cmd.key })));
  return (
    <>
      <button type="button" class="btn btn-primary" onClick={go}>
        {cmd.label} – {top.name}
      </button>
      {sending ? <SendForReview api={p.api} projectId={p.v.id} videoId={sending.id} videoName={sending.name} fallback={() => p.run(() => p.api.post<Change>(`/api/projects/${p.v.id}/videos/${sending.id}/command`, { command: 'submit_internal' }))} onDone={() => p.api.get<ProjectDetail>(`/api/projects/${p.v.id}`).then((project) => p.run(async () => ({ project, text: '' })))} onClose={() => setSending(null)} /> : null}
    </>
  );
}

export function Videos(p: { api: Api; v: ProjectDetail; run: Run }) {
  const lang = useLang();
  const t = TX[lang];
  const { v } = p;
  const [reasonFor, setReasonFor] = useState<{ video: VideoView; cmd: Command } | null>(null);
  const [editing, setEditing] = useState<VideoView | null>(null);
  const [editorFor, setEditorFor] = useState<{ video: VideoView; at: HTMLElement } | null>(null);
  const [sending, setSending] = useState<{ video: VideoView; cmd: Command } | null>(null);
  const [removing, setRemoving] = useState<VideoView | null>(null);
  const command = (video: VideoView, cmd: Command, extra: Record<string, unknown> = {}) => p.run(() => p.api.post<Change>(`/api/projects/${v.id}/videos/${video.id}/command`, { command: cmd.key, ...extra }));
  // "Skicka till intern granskning" picks the file too (owner 2026-11-01): file and status in one step
  const press = (video: VideoView, cmd: Command) => (cmd.key === 'submit_internal' ? setSending({ video, cmd }) : void command(video, cmd));
  useHashFocus(true);
  const live = v.videos.filter((x) => x.step !== 'cancelled');
  const dropped = v.videos.filter((x) => x.step === 'cancelled');
  const card = (x: VideoView) => {
    // the viewer's own next command is the card's one visible button; the rest sit in ⋯
    const main = !x.next.mine ? undefined : x.forward !== undefined ? x.commands.find((c) => c.key === x.forward) : x.commands.find((c) => !c.needsReason && !BACKWARD.includes(c.key));
    const rest = x.commands.filter((c) => c !== main);
    const due = x.step === 'customer_changes' ? x.nextDraftDue : x.next.due;
    return (
      <article class="video-card" id={`video-${x.id}`} aria-labelledby={`vc-${x.id}`}>
        <div class="video-card-head">
          <h3 id={`vc-${x.id}`} title={x.code ? `${x.name} · ${x.code}` : x.name}>
            {/* the name opens the video's review (owner 2026-11-01): what people do most, one click */}
            <a class="vc-name" href={`/projects/${v.id}/review/${x.id}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${v.id}/review/${x.id}`))}>
              {breakable(x.name)}
            </a>
          </h3>
          <RowMenu
            label={t.moreFor(x.name)}
            items={[
              ...rest.map((c) => ({ label: c.label, danger: c.key === 'cancel', run: () => (c.needsReason ? setReasonFor({ video: x, cmd: c }) : press(x, c)) })),
              { label: t.openReview, run: () => navigate(`/projects/${v.id}/review/${x.id}`) },
              ...(v.can.edit ? [{ label: t.editVideoMenu, run: () => setEditing(x) }] : []),
              ...(v.can.removeVideo ? [{ label: removeTexts[lang].removeVideoMenu, danger: true, run: () => setRemoving(x) }] : [])
            ]}
          />
        </div>
        {/* one status: the bar with its stage, then who has it now (or the exact step); Monday's own label on hover */}
        <StageBar stage={x.stage} compact />
        {/* the line under the bar only when it says more than the bar's own label (a cancelled video said "Cancelled" twice) */}
        {x.next.mine || x.next.who?.length || x.stage.detail.replace(`${x.stage.label} · `, '') !== x.stage.label ? (
        <p class="next-step-text" title={x.mondayLabel ? t.inMonday(x.mondayLabel) : undefined}>
          {x.next.who?.length && !x.next.mine ? <AvatarStack people={x.next.who} max={3} size="sm" label={t.waitingOn} /> : null}
          <span>{x.next.mine ? <strong>{x.next.phrase}</strong> : x.next.who?.length ? x.next.phrase : x.stage.detail.replace(`${x.stage.label} · `, '')}</span>
        </p>
        ) : null}
        {x.overrideReason ? (
          <span class="flag" title={x.overrideReason}>
            {t.forced(x.overrideReason)}
          </span>
        ) : null}
        <div class="video-card-foot">
          {v.can.edit ? (
            <button
              type="button"
              class={`vc-editor${editorFor?.video.id === x.id ? ' is-open' : ''}`}
              aria-label={`${t.editor}: ${x.editor?.name ?? t.noEditor}. ${t.changeRole(t.editor)}`}
              aria-expanded={editorFor?.video.id === x.id}
              onClick={(e) => setEditorFor(editorFor?.video.id === x.id ? null : { video: x, at: e.currentTarget as HTMLElement })}
            >
              {x.editor ? (
                <PersonChip name={x.editor.name} sub={t.editor} />
              ) : (
                <span class="pchip">
                  <AddBall size="sm" />
                  <span>{t.noEditor}</span>
                </span>
              )}
            </button>
          ) : x.editor ? (
            <PersonChip name={x.editor.name} sub={t.editor} />
          ) : (
            <span>{t.noEditor}</span>
          )}
          {main ? (
            <button type="button" class="btn btn-sm" onClick={() => press(x, main)}>
              {main.label}
            </button>
          ) : due ? (
            <span>{dueText(due, lang)}</span>
          ) : null}
        </div>
      </article>
    );
  };
  if (!v.videos.length) {
    return (
      <div class="empty">
        <strong>{t.noVideos}</strong>
        <span class="muted">{t.addVideosHint}</span>
      </div>
    );
  }
  return (
    <section class="blk" aria-labelledby="pv-h" data-tour="videos">
      <h2 class="blk-title" id="pv-h">
        {t.videos} <span class="fold-n">{live.length}</span>
      </h2>
      <div class="video-cards">
        {live.map(card)}
      </div>
      {/* dropped videos keep their history, folded away under the live ones (owner 2026-11-02) */}
      {dropped.length ? (
        <details class="fold vc-dropped">
          <summary>
            <span>{t.droppedVideos}</span> <span class="fold-n">{dropped.length}</span>
          </summary>
          <div class="video-cards">{dropped.map(card)}</div>
        </details>
      ) : null}
      {editorFor ? <VideoEditorPicker api={p.api} projectId={v.id} video={editorFor.video} at={editorFor.at} run={p.run} onClose={() => setEditorFor(null)} /> : null}
      {reasonFor ? <ReasonDialog api={p.api} video={reasonFor.video} cmd={reasonFor.cmd} onClose={() => setReasonFor(null)} onRun={(extra) => command(reasonFor.video, reasonFor.cmd, extra)} /> : null}
      {sending ? <SendForReview api={p.api} projectId={v.id} videoId={sending.video.id} videoName={sending.video.name} fallback={() => command(sending.video, sending.cmd)} onDone={() => p.api.get<ProjectDetail>(`/api/projects/${v.id}`).then((project) => p.run(async () => ({ project, text: '' })))} onClose={() => setSending(null)} /> : null}
      {editing ? <EditVideoDialog api={p.api} project={v} video={editing} run={p.run} onClose={() => setEditing(null)} /> : null}
      {removing ? <RemoveVideoDialog<ProjectDetail> api={p.api} projectId={v.id} video={removing} onClose={() => setRemoving(null)} onDone={(r) => void p.run(async () => r)} /> : null}
    </section>
  );
}

/** Reopen, Cancel and Force stage: a reason (kept in History); Force also picks phase and step. */
function ReasonDialog(p: { api: Api; video: VideoView; cmd: Command; onClose: () => void; onRun: (extra: Record<string, unknown>) => Promise<void> }) {
  const t = TX[useLang()];
  const o = useOptions(p.api);
  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState(p.video.phase);
  const [step, setStep] = useState(p.video.step);
  const force = p.cmd.key === 'force';
  const steps = o?.phases.find((x) => x.phase === phase)?.steps ?? [];
  return (
    <Dialog
      title={`${p.cmd.label.replace('…', '')} – ${p.video.name}`}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="button"
            class={`btn ${p.cmd.key === 'cancel' ? 'btn-danger' : 'btn-primary'}`}
            disabled={!reason.trim() || (force && !steps.includes(step))}
            onClick={async () => {
              await p.onRun({ reason, ...(force ? { to: { phase, step } } : {}) });
              p.onClose();
            }}
          >
            {p.cmd.label.replace('…', '')}
          </button>
        </>
      }
    >
      <div class="stack">
        {force ? (
          <div class="grid2">
            <div class="field">
              <label class="label" for="fs-phase">
                {t.phase}
              </label>
              <select id="fs-phase" class="input input--wide" value={phase} onChange={(e) => setPhase(val(e))}>
                {(o?.phases ?? []).map((x) => (
                  <option value={x.phase}>{t.phases[x.phase] ?? x.phase}</option>
                ))}
              </select>
            </div>
            <div class="field">
              <label class="label" for="fs-step">
                {t.stepLabel}
              </label>
              <select id="fs-step" class="input input--wide" value={step} onChange={(e) => setStep(val(e))}>
                {steps.map((s) => (
                  <option value={s}>{t.step[s] ?? s}</option>
                ))}
              </select>
            </div>
          </div>
        ) : null}
        <div class="field">
          <label class="label" for="rs-reason">
            {t.reason}
          </label>
          <textarea id="rs-reason" class="textarea" rows={3} maxLength={500} value={reason} onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)} />
          <span class="hint">{t.reasonHint}</span>
        </div>
      </div>
    </Dialog>
  );
}

/** One video's editor, straight from its card (owner 2026-11-01): the same search list, one person, saved at once. */
function VideoEditorPicker(p: { api: Api; projectId: string; video: VideoView; at: HTMLElement; run: Run; onClose: () => void }) {
  const t = TX[useLang()];
  const o = useOptions(p.api);
  return (
    <PersonPicker
      label={t.editor}
      people={o ? o.people.editor : null}
      current={p.video.editor ? [p.video.editor.id] : []}
      multi={false}
      at={p.at}
      empty={t.nobodyHasRole(t.editor)}
      onClose={p.onClose}
      onPick={(ids) => p.run(() => p.api.put<Change>(`/api/projects/${p.projectId}/videos/${p.video.id}`, { editorId: ids[0] ?? null }))}
    />
  );
}

function EditVideoDialog(p: { api: Api; project: ProjectDetail; video: VideoView; run: Run; onClose: () => void }) {
  const t = TX[useLang()];
  const o = useOptions(p.api);
  const x = p.video;
  const [name, setName] = useState(x.name);
  const [editorId, setEditorId] = useState(x.editor?.id ?? '');
  const [internal, setInternal] = useState(x.internalDeadline ?? '');
  const [firstDraft, setFirstDraft] = useState(x.firstDraftDue ?? '');
  const [finals, setFinals] = useState(x.needsFinalFormats);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (busy) return;
    const body: Record<string, unknown> = {};
    if (name !== x.name) body.name = name;
    if (editorId !== (x.editor?.id ?? '')) body.editorId = editorId || null;
    if (internal !== (x.internalDeadline ?? '')) body.internalDeadline = internal || null;
    if (firstDraft !== (x.firstDraftDue ?? '')) body.firstDraftDue = firstDraft || null;
    if (finals !== x.needsFinalFormats) body.needsFinalFormats = finals;
    setBusy(true);
    if (Object.keys(body).length) await p.run(() => p.api.put<Change>(`/api/projects/${p.project.id}/videos/${x.id}`, body));
    p.onClose();
  };
  return (
    <Dialog
      title={t.editTitle(x.name)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {t.save}
          </button>
        </>
      }
    >
      <div class="stack">
        <div class="field">
          <label class="label" for="ev-name">
            {t.name}
          </label>
          <input id="ev-name" class="input input--wide" value={name} onInput={(e) => setName(val(e))} />
        </div>
        <div class="field">
          <label class="label" for="ev-editor">
            {t.editor}
          </label>
          <select id="ev-editor" class="input input--wide" value={editorId} onChange={(e) => setEditorId(val(e))}>
            <option value="">{t.nobodyYet}</option>
            {(o?.people.editor ?? []).map((e) => (
              <option value={e.id}>
                {e.name}
                {e.freelancer ? t.freelanceParen : ''}
              </option>
            ))}
          </select>
        </div>
        <MoreOptions id="edit-video" filled={(internal ? 1 : 0) + (firstDraft ? 1 : 0)}>
          <div class="grid2">
            <div class="field">
              <label class="label" for="ev-internal">
                {t.internalDeadline}
              </label>
              <input id="ev-internal" class="input input--date" type="date" value={internal} onInput={(e) => setInternal(val(e))} />
            </div>
            <div class="field">
              <label class="label" for="ev-first">
                {t.firstDraftDue}
              </label>
              <input id="ev-first" class="input input--date" type="date" value={firstDraft} onInput={(e) => setFirstDraft(val(e))} />
            </div>
          </div>
          {x.phase === 'draft' && x.step !== 'delivered' ? (
            <label class="check">
              <input type="checkbox" checked={finals} onChange={(e) => setFinals((e.target as HTMLInputElement).checked)} /> {t.finalsAfter}
            </label>
          ) : null}
        </MoreOptions>
      </div>
    </Dialog>
  );
}

function Details(p: { api: Api; v: ProjectDetail; run: Run; crew?: boolean; set: (v: ProjectDetail) => void }) {
  const { v } = p;
  const lang = useLang();
  const t = TX[lang];
  const o = useOptions(p.api);
  const [teamRole, setTeamRole] = useState<{ role: string; label: string; at: HTMLElement } | null>(null);
  const [addingContact, setAddingContact] = useState(false);
  const [editingContact, setEditingContact] = useState<EditableContact | null>(null);
  const [portalFor, setPortalFor] = useState<Contact | null>(null);
  const put = (body: Record<string, unknown>) => p.run(() => p.api.put<Change>(`/api/projects/${v.id}`, body));
  const edit = v.can.edit;
  const live = v.videos.filter((x) => x.step !== 'cancelled');
  const delivered = live.filter((x) => x.step === 'delivered').length;
  const sel = (id: string, value: string, options: [string, string][], onPick: (v: string) => void) =>
    edit ? (
      <select id={id} class="input input--quiet" value={value} onChange={(e) => onPick(val(e))}>
        {options.map(([k, l]) => (
          <option value={k}>{l}</option>
        ))}
      </select>
    ) : (
      <span>{options.find(([k]) => k === value)?.[1] ?? value}</span>
    );
  const contacts = v.contacts.filter((c) => c.active);
  return (
    <div class="pd">
      <div class="pd-main">
      <section class="blk pd-about" aria-labelledby="pd-h">
        <h2 class="blk-title" id="pd-h">
          {t.about}
        </h2>
        <dl class="props">
          <Prop label={t.name} for={edit ? 'pd-name' : undefined}>
            {edit ? <RenameProject name={v.name} onSave={(name) => put({ name })} /> : v.name}
          </Prop>
          <Prop label={t.client}>
            {p.crew ? (
              v.client.name
            ) : (
              <a href={`/clients/${v.client.id}`} onClick={go(`/clients/${v.client.id}`)}>
                {v.client.name}
              </a>
            )}
          </Prop>
          <Prop label={t.projectCode}>
            <span class="pj-code">{v.code}</span>
          </Prop>
          <Prop label={t.deliveryMonth} for={edit ? 'pd-month' : undefined}>
            {edit ? <input id="pd-month" class="input input--quiet" type="month" value={v.deliveryMonth} onChange={(e) => val(e) && void put({ deliveryMonth: val(e) })} /> : monthLabel(v.deliveryMonth, lang)}
          </Prop>
          <Prop label={t.productionType} for={edit ? 'pd-type' : undefined}>
            {sel('pd-type', v.productionType, (o?.productionTypes ?? [v.productionType]).map((x) => [x, t.type[x] ?? x]), (x) => void put({ productionType: x }))}
          </Prop>
          {/* package and booking are the producer's (owner 2026-10-21: editing and camera see what they work from) */}
          {edit ? (
            <>
          <Prop label={t.package} for={edit ? 'pd-pkg' : undefined}>
            {sel('pd-pkg', v.creativePackage ?? '', [['', t.notSet], ...(o?.packages ?? (v.creativePackage ? [v.creativePackage] : [])).map((x): [string, string] => [x, x])], (x) => void put({ creativePackage: x || null }))}
          </Prop>
          <ProjectAgreement api={p.api} clientId={v.client.id} />
          <Prop label={t.booking} for={edit ? 'pd-booking' : undefined}>
            {sel('pd-booking', v.bookingStatus, (o?.bookingStatuses ?? [v.bookingStatus]).map((x) => [x, t.bookings[x] ?? x]), (x) => void put({ bookingStatus: x }))}
          </Prop>
            </>
          ) : null}
          <Prop label={t.stage}>
            <StageBar stage={v.stage} />
          </Prop>
          <Prop label={t.videos}>
            {live.length ? t.videosOf(delivered, live.length) : t.noneYetShort}
          </Prop>
          {/* the billing company sits in Leverans; a Monday project keeps it here (it is still picked in the Hub) */}
          {v.billTo && v.mondayOwned ? (
            <Prop label={t.billTo} for={edit ? 'pd-billto' : undefined}>
              {sel(
                'pd-billto',
                v.billTo.current ?? '',
                [['', t.billToMain(v.billTo.main)], ...v.billTo.companies.map((c): [string, string] => [c.id, c.name])],
                (x) => void p.run(async () => ({ ...(await p.api.put<{ project: ProjectDetail }>(`/api/projects/${v.id}/bill-to`, { customerId: x || null })), text: t.clientMailSaved }))
              )}
            </Prop>
          ) : null}
          {v.clientMail ? (
            <Prop label={t.clientMail} for={v.can.edit || v.mondayOwned ? 'pd-mail' : undefined}>
              <ClientMailChoice api={p.api} v={v} run={p.run} />
            </Prop>
          ) : null}
          {v.fromMonday ? <Prop label={t.source}>{t.importedMonday}</Prop> : null}
          {v.mondayMirror ? (
            <Prop label={t.mirror}>
              <div class="pd-mirror">
                {v.mondayMirror.itemUrl ? (
                  <a href={v.mondayMirror.itemUrl} target="_blank" rel="noopener noreferrer">
                    {t.mirrorOpen}
                  </a>
                ) : (
                  <span class="muted">{t.mirrorPending}</span>
                )}
                {v.mondayMirror.error ? <p class="small warn">{t.mirrorFailed(v.mondayMirror.error)}</p> : v.mondayMirror.pushedAt ? <p class="small muted">{t.mirrorSynced(new Date(v.mondayMirror.pushedAt).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }))}</p> : null}
                {v.mondayMirror.warnings.map((w) => (
                  <p class="small muted">{w}</p>
                ))}
              </div>
            </Prop>
          ) : null}
        </dl>
      </section>
      <DeliveryBox api={p.api} v={v} onSaved={p.set} />
      </div>
      <div class="pd-side">
        <section class="blk" aria-labelledby="pt-h">
          <h2 class="blk-title" id="pt-h">
            {t.team}
          </h2>
          <ul class="tl">
            {v.team.map((r) => {
              const settable = canSetRole(v, r.role);
              const inner = (
                <>
                  <span class="tl-k">
                    <RoleMark role={r.role} />
                    {r.label}
                  </span>
                  <span class="tl-v">
                    {r.people.length ? (
                      r.people.map((x) => (
                        <span class="tl-p">
                          <Avatar name={x.name} size="sm" decorative />
                          <span>
                            {x.name}
                            {x.isPrimary && r.people.length > 1 ? <small> {t.primaryLower}</small> : null}
                          </span>
                        </span>
                      ))
                    ) : settable ? (
                      <span class="tl-empty">
                        <AddBall size="sm" />
                        {t.add}
                      </span>
                    ) : (
                      <span class="muted">{t.nobodyYet}</span>
                    )}
                  </span>
                  <span class="tl-car" aria-hidden="true">
                    {settable ? <Icon name="chevron-down" /> : v.mondayOwned ? <Icon name="lock" /> : null}
                  </span>
                </>
              );
              return (
                <li>
                  {settable ? (
                    <button
                      type="button"
                      class={`tl-row${teamRole?.role === r.role ? ' is-open' : ''}`}
                      aria-label={t.changeRole(r.label)}
                      aria-expanded={teamRole?.role === r.role}
                      onClick={(e) => setTeamRole(teamRole?.role === r.role ? null : { role: r.role, label: r.label, at: e.currentTarget as HTMLElement })}
                    >
                      {inner}
                    </button>
                  ) : (
                    <div class="tl-row is-static">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
          {v.mondayOwned ? (
            <p class="tl-note">
              <Icon name="lock" />
              {t.teamInMonday}
            </p>
          ) : null}
        </section>
        <section class="blk" aria-labelledby="pc-h">
          <div class="blk-head">
            <h2 class="blk-title" id="pc-h">
              {t.clientContacts}
            </h2>
            {v.can.editContacts ? (
              <button type="button" class="btn btn-ghost btn-sm" onClick={() => setAddingContact(true)}>
                {t.addContactPlus}
              </button>
            ) : null}
          </div>
          {!v.decider && contacts.length ? <Callout tone="warn" title={t.missingDecider}>{t.pickDecider}</Callout> : null}
          {contacts.length ? (
            <ul class="pd-contacts">
              {contacts.map((c) => {
                const items = [
                  ...(edit && v.decider?.id !== c.id ? [{ label: t.makeDecider, run: () => void put({ deciderContactId: c.id }) }] : []),
                  ...(v.can.editContacts ? [{ label: t.edit, run: () => setEditingContact(c) }, { label: t.portalLinkMenu, run: () => setPortalFor(c) }] : []),
                  // an admin sees the portal as this contact does, without touching their link (read only)
                  ...(viewAsAllowed() && c.active ? [{ label: t.viewAsClient, run: () => void startViewAs(p.api, { contactId: c.id }).catch((e) => toast({ text: e instanceof Error ? e.message : String(e) })) }] : [])
                ];
                return (
                  <li class="pd-contact">
                    <PersonChip name={c.name ?? c.email} sub={c.name && c.email ? c.email : undefined} client />
                    <span class="pd-contact-end">
                      {v.decider?.id === c.id ? <span class="pill pill--info">{t.decider}</span> : null}
                      {items.length ? <RowMenu label={t.moreFor(c.name ?? c.email)} items={items} /> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p class="muted">{t.noContacts}</p>
          )}
        </section>
      </div>
      {teamRole ? (
        <RolePicker
          api={p.api}
          projectId={v.id}
          role={teamRole.role}
          label={teamRole.label}
          current={v.team.find((x) => x.role === teamRole.role)?.people.map((x) => x.id) ?? []}
          at={teamRole.at}
          onClose={() => setTeamRole(null)}
          onSaved={(r) => void p.run(async () => r)}
        />
      ) : null}
      {portalFor ? <PortalLinkDialog api={p.api} clientId={v.client.id} contact={portalFor} onClose={() => setPortalFor(null)} /> : null}
      {addingContact ? <ContactDialog api={p.api} clientId={v.client.id} onClose={() => setAddingContact(false)} onAdded={() => p.api.get<ProjectDetail>(`/api/projects/${v.id}`).then((project) => p.run(async () => ({ project, text: t.contactAdded })))} /> : null}
      {editingContact ? <ContactDialog api={p.api} contact={editingContact} onClose={() => setEditingContact(null)} onAdded={() => p.api.get<ProjectDetail>(`/api/projects/${v.id}`).then((project) => p.run(async () => ({ project, text: t.contactSaved })))} /> : null}
    </div>
  );
}

/**
 * How much the Hub emails the client in this project (owner 2026-10-24): as the client, on, no reminders or off. The
 * strictest of the company, the client card and the project applies, and the row says when it is not the project's.
 */
function ClientMailChoice(p: { api: Api; v: ProjectDetail; run: Run }) {
  const t = TX[useLang()];
  const m = p.v.clientMail!;
  if (m.ziflow) return <span class="muted">{t.clientMailZiflow}</span>;
  // the producer may set it on a project Monday owns too: it is the Hub's own field
  const editable = p.v.can.edit || p.v.mondayOwned;
  const label = (k: string) => {
    const x = t.clientMailOpt[k]!;
    return typeof x === 'function' ? x(t.clientMailLevel[m.client]) : x;
  };
  const value = m.project ?? '';
  return (
    <div class="pd-mail">
      {editable ? (
        <select
          id="pd-mail"
          class="input input--quiet"
          value={value}
          onChange={(e) => {
            const x = (e.currentTarget as HTMLSelectElement).value;
            void p.run(async () => ({ ...(await p.api.put<{ project: ProjectDetail }>(`/api/projects/${p.v.id}/client-mail`, { mail: x || null })), text: t.clientMailSaved }));
          }}
        >
          {['', 'on', 'no_reminders', 'off'].map((k) => (
            <option value={k}>{label(k || 'inherit')}</option>
          ))}
        </select>
      ) : (
        <span>{label(value || 'inherit')}</span>
      )}
      {/* someone else's stricter choice wins: say so (as the client, the option already names the client's choice) */}
      {m.by === 'company' || (m.by === 'client' && m.project !== null) ? <p class="small warn">{t.clientMailBy(m.by)}</p> : null}
      <p class="small muted">{t.clientMailHint}</p>
    </div>
  );
}

/** One row of the project's facts: label left, value (or its control) right. */
/**
 * The client's agreement in force on the project page (owner 2026-10-27): what the project is called off under, or a
 * warning when there is none. Only for those who see prices; for everyone else the request is refused and nothing shows.
 */
function ProjectAgreement(p: { api: Api; clientId: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<AgreementRow[] | null>(null);
  useEffect(() => {
    p.api.get<{ agreements: AgreementRow[] }>('/api/agreements').then((r) => setRows(r.agreements), () => setRows(null));
  }, [p.clientId]);
  if (!rows) return null;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
  const c = contractsOf(rows, today).find((x) => x.state === 'active' && x.a.client?.id === p.clientId);
  if (!c)
    return (
      <Prop label={t.agreementLbl}>
        <span class="contract-warn">{t.noAgreement}</span>{' '}
        <a href="/agreements/new" onClick={(e) => (e.preventDefault(), navigate('/agreements/new'))}>
          {t.createQuote}
        </a>
      </Prop>
    );
  const line = agreementLine(contractFact(c), today, lang);
  return (
    <Prop label={t.agreementLbl}>
      <a href="/contracts" onClick={(e) => (e.preventDefault(), navigate('/contracts'))}>
        {contractWhat(c.a, lang)}
      </a>
      <span class={`cell-sub${line.tone === 'warn' || line.tone === 'crit' ? ` contract-${line.tone}` : ''}`}>{line.tone === 'ok' ? (line.detail ?? line.label) : line.label}</span>
    </Prop>
  );
}

function Prop(p: { label: string; for?: string; children: ComponentChildren }) {
  return (
    <div class="prop">
      <dt>{p.for ? <label for={p.for}>{p.label}</label> : p.label}</dt>
      <dd>{p.children}</dd>
    </div>
  );
}

function RenameProject(p: { name: string; onSave: (name: string) => Promise<void> }) {
  const t = TX[useLang()];
  const [name, setName] = useState(p.name);
  useEffect(() => setName(p.name), [p.name]);
  const dirty = name.trim() !== '' && name !== p.name;
  return (
    <form
      class="pd-rename"
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty) void p.onSave(name);
      }}
    >
      <input id="pd-name" class="input input--quiet" value={name} onInput={(e) => setName(val(e))} />
      {dirty ? (
        <button type="submit" class="btn btn-sm">
          {t.rename}
        </button>
      ) : null}
    </form>
  );
}

interface PortalLinkStatus {
  state: 'none' | 'active' | 'revoked' | 'expired';
  text: string;
}

/** The client portal's personal link for one contact: copy a new one (stops the previous) and send it yourself, or revoke. */
function PortalLinkDialog(p: { api: Api; clientId: string; contact: Contact; onClose: () => void }) {
  const t = TX[useLang()];
  const base = `/api/clients/${p.clientId}/contacts/${p.contact.id}/portal-link`;
  const [link, setLink] = useState<PortalLinkStatus | null>(null);
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const [sure, setSure] = useState(false);
  const who = p.contact.name ?? p.contact.email;
  useEffect(() => {
    p.api.get<{ link: PortalLinkStatus }>(base).then((r) => setLink(r.link), (e) => setErr(errText(e)));
  }, [base]);
  const copy = async () => {
    try {
      const r = await p.api.post<{ url: string; link: PortalLinkStatus }>(base);
      setUrl(r.url);
      setLink(r.link);
      try {
        await navigator.clipboard?.writeText(r.url);
        toast({ text: t.portalCopied });
      } catch {
        toast({ text: t.portalCreated });
      }
    } catch (e) {
      setErr(errText(e));
    }
  };
  const revoke = async () => {
    try {
      const r = await p.api.post<{ link: PortalLinkStatus }>(`${base}/revoke`);
      setUrl('');
      setLink(r.link);
      setSure(false);
      toast({ text: t.portalRevoked(who) });
    } catch (e) {
      setErr(errText(e));
    }
  };
  return (
    <Dialog
      title={t.portalTitle(who)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn btn-danger" disabled={link?.state !== 'active' || sure} onClick={() => setSure(true)}>
            {t.revokeLink}
          </button>
          <button type="button" class="btn btn-primary" onClick={() => void copy()}>
            {link?.state === 'active' ? t.copyNewLink : t.copyLink}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.portalBody(who)}</p>
        <p class="small muted">{link ? link.text : t.loading}</p>
        {url ? (
          <div class="field">
            <label class="label" for="pl-portal-url">
              {t.newLinkFor(who)}
            </label>
            <input class="input input--wide" id="pl-portal-url" readOnly value={url} onFocus={(e) => (e.target as HTMLInputElement).select()} />
          </div>
        ) : null}
        {sure ? (
          <div class="callout callout--crit error-box" role="alert">
            <span>{t.revokeSure(who)}</span>
            <span class="cluster">
              <button type="button" class="btn btn-sm" onClick={() => setSure(false)}>
                {t.cancel}
              </button>
              <button type="button" class="btn btn-sm btn-danger-solid" onClick={() => void revoke()}>
                {t.revokeLink}
              </button>
            </span>
          </div>
        ) : null}
        {err ? <p class="field-error">{err}</p> : null}
      </div>
    </Dialog>
  );
}

/** Add a contact on a client; from the Contacts tab (no client yet) the dialog asks which company first. */
/** A contact as the edit dialog needs it (Clients › Contacts, the client card, the project page) */
export interface EditableContact {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  title?: string | null;
  canApprove?: boolean;
  billing?: boolean;
  active?: boolean;
}

/** Adds a contact, or with `contact` corrects one: address, name, title, phone, roles, or marks them former. */
export function ContactDialog(p: { api: Api; clientId?: string; clients?: { id: string; name: string }[]; contact?: EditableContact; onClose: () => void; onAdded: () => void }) {
  const t = TX[useLang()];
  const c0 = p.contact;
  const [busy, setBusy] = useState(false);
  const [clientId, setClientId] = useState(p.clientId ?? '');
  const [name, setName] = useState(c0?.name ?? '');
  // the name follows the address until someone types one (anna.svensson@ → Anna Svensson); an existing contact keeps theirs
  const [nameTyped, setNameTyped] = useState(Boolean(c0));
  const [email, setEmail] = useState(c0?.email ?? '');
  const [phone, setPhone] = useState(c0?.phone ?? '');
  const [jobTitle, setJobTitle] = useState(c0?.title ?? '');
  const [approve, setApprove] = useState(c0?.canApprove ?? false);
  const [billing, setBilling] = useState(c0?.billing ?? false);
  const [former, setFormer] = useState(c0 ? c0.active === false : false);
  const [err, setErr] = useState('');
  const label = c0 ? t.save : t.addContact;
  return (
    <Dialog
      title={c0 ? t.editContact : t.addContact}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            disabled={busy}
            onClick={async () => {
              if (!c0 && !clientId) return setErr(t.pickCompany);
              setBusy(true);
              try {
                if (c0) await p.api.put(`/api/clients/contacts/${c0.id}`, { name, email, phone, title: jobTitle, canApprove: approve, billing, active: !former });
                else await p.api.post(`/api/clients/${clientId}/contacts`, { name, email, phone, title: jobTitle, canApprove: approve });
                p.onAdded();
                p.onClose();
              } catch (e) {
                setErr(errText(e));
                setBusy(false);
              }
            }}
          >
            {label}
          </button>
        </>
      }
    >
      <div class="stack">
        {p.clients && !c0 ? (
          <div class="field">
            <label class="label" for="ct-client">
              {t.company}
            </label>
            <select id="ct-client" class="input input--wide" value={clientId} onChange={(e) => setClientId(val(e))}>
              <option value="">{t.pickCompany}</option>
              {p.clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div class="field">
          <label class="label" for="ct-email">
            {t.email}
          </label>
          <input
            id="ct-email"
            class="input input--wide"
            type="email"
            value={email}
            onInput={(e) => {
              setEmail(val(e));
              if (!nameTyped) setName(nameFromEmail(val(e)) ?? '');
            }}
            aria-invalid={err ? true : undefined}
          />
          {err ? <span class="field-error">{err}</span> : null}
        </div>
        <div class="field">
          <label class="label" for="ct-name">
            {t.name}
          </label>
          <input
            id="ct-name"
            class="input input--wide"
            value={name}
            onInput={(e) => {
              setName(val(e));
              setNameTyped(true);
            }}
          />
          {name && !nameTyped ? <span class="hint">{t.contactNameHint}</span> : null}
        </div>
        <MoreOptions id="add-contact" filled={(phone.trim() ? 1 : 0) + (jobTitle.trim() ? 1 : 0) + (approve ? 1 : 0) + (billing ? 1 : 0) + (former ? 1 : 0)}>
          <div class="field">
            <label class="label" for="ct-title">
              {t.jobTitle}
            </label>
            <input id="ct-title" class="input input--wide" value={jobTitle} onInput={(e) => setJobTitle(val(e))} />
          </div>
          <div class="field">
            <label class="label" for="ct-phone">
              {t.phone}
            </label>
            <input id="ct-phone" class="input input--wide" type="tel" value={phone} onInput={(e) => setPhone(val(e))} />
          </div>
          <label class="check">
            <input type="checkbox" checked={approve} onChange={(e) => setApprove((e.target as HTMLInputElement).checked)} /> {t.canApprove}
          </label>
          {c0 ? (
            <>
              <label class="check">
                <input type="checkbox" checked={billing} onChange={(e) => setBilling((e.target as HTMLInputElement).checked)} /> {t.billingContact}
              </label>
              <label class="check">
                <input type="checkbox" checked={former} onChange={(e) => setFormer((e.target as HTMLInputElement).checked)} /> {t.formerContact}
              </label>
            </>
          ) : null}
        </MoreOptions>
      </div>
    </Dialog>
  );
}
